import {
  AgentEvaluationDetail,
  AgentEvaluationPickerFilter,
  AgentEvaluationPickerRow,
  AgentEvaluationReportDetail,
  AgentEvaluationRunDetail,
  AgentEvaluationRunSummary,
  AgentEvaluationSummary,
  AgentMessageEventDetail,
  AgentModelOption,
  AgentPlanEventDetail,
  AgentQuestionEventDetail,
  AgentSessionDetail,
  AgentSessionEventDetail,
  AgentSessionMetrics,
  AgentSessionStatus,
  AgentSubAgentResponseEventDetail,
  AgentTypeInfo,
  Answers,
  SessionConfigDescriptor as ApiSessionConfigDescriptor,
  AssertionSeverity,
  CHAT_EVAL_AGENT_TYPE,
  ChatTranscriptResponse,
  CreateEvaluationPayload,
  EnhancedAgentSessionMetrics,
  EvaluationAssertion,
  EvaluationChatConfig,
  EvaluationDriverConfig,
  EvaluationLaunchTool,
  EvaluationMemory,
  EvaluationOrganizationOption,
  EvaluationRunStatus,
  EvaluationSessionConfig,
  EvaluationTripConfig,
  EvaluationTripStop,
  EvaluationUserLookup,
  GetAgentEvaluationPickerResponse,
  isAgentQuestionEvent,
  isAgentSubAgentResponseEvent,
  isAgentToolCallResultEvent,
  PagingMeta,
  PlaygroundPermissions,
  RunEvaluationsBatchSelection,
  SessionFormData,
  ThinkingLevel,
} from "@/types/agent"
import { FlightQuoteDetails } from "@/types/flight-quote-details"
import { FlightLegApi } from "@/types/flights/flight-leg"
import {
  AgentStatusUpdateEventData,
  AgentStreamChunkEventData,
} from "@/types/socket"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { FlightOptionWithMeta } from "../agent-api"
import {
  defaultModelIdForAgentType,
  filterAllowedModelsForAgentType,
  resolveDefaultThinkingLevel,
  ThinkingOverridePreset,
} from "../agent-models"
import { nowISOString, nowMillis } from "../date-utils"
import { RootState } from "./redux-store"

// Cache entry types
export interface FlightOptionCacheEntry {
  data: FlightOptionWithMeta | null
  loading: boolean
  error: string | null
}

export interface FlightQuoteCacheEntry {
  data: FlightQuoteDetails | null
  loading: boolean
  error: string | null
}

export type ViewMode =
  | "inline"
  | "stats"
  | "triage"
  | "notes"
  | "entity_notes"
  | "todos"
  | "progress"
  | "sandbox"
  | "raw"
  | "sub_agents"
  | "report"
  | "interactive_report"

export const emptySessionDetail = (): AgentSessionDetail => ({
  id: 0,
  uuid: "",
  status: "ready",
  agent_type: "Agent::DefaultOrchestrator",
  default_model_name: "",
  created_at: {
    epoch_millis: 0,
  },
  updated_at: {
    epoch_millis: 0,
  },
  title: "New Session",
  message_count: 0,
  tool_call_count: 0,
  metadata: null,
  user_id: 0,
  max_iterations: null,
  total_cost: 0,
  shared: false,
})

export const emptyMetrics = (): AgentSessionMetrics => ({
  total_input_tokens: 0,
  total_output_tokens: 0,
  total_cost: 0,
  message_count: 0,
  tool_call_count: 0,
  total_cache_read_tokens: 0,
  total_cache_write_tokens: 0,
})

export const emptySessionForm = (): SessionFormData => ({
  selectedAgentType: "Agent::MiniAgent",
  selectedModelId: null,
  availableModels: [],
  maxIterations: 100,
  message: "",
  thinkingLevel: "default",
  shared: true,
  sessionConfigOverrides: {},
})

type NewSessionState = {
  type: "new"
}

type LoadingSessionState = {
  type: "loading"
  sessionId: number
}

type ExistingSessionState = {
  type: "existing"
  session: AgentSessionDetail
  events: AgentSessionEventDetail[]
  eventsLoading: boolean
  hasMoreEvents: boolean
  statusUpdate: AgentStatusUpdateEventData | null
  statusMessage: string | null // Current status message from agent
}

type ErrorState = {
  type: "error"
  error: string
  failedSessionId?: number
}

type AgenticDetailPageState = {
  loading: boolean
  sendingMessage: boolean
  stoppingSession: boolean
  messageError: string | null // Error when sending a message
  availableAgentTypes: AgentTypeInfo[]
  metrics: AgentSessionMetrics
  enhancedMetrics: EnhancedAgentSessionMetrics | null
  metricsLoading: boolean
  metricsError: string | null
  metricsExpanded: boolean
  sessionForm: SessionFormData
  expandedContentStates: Record<string, boolean>
  showRejectInput: boolean // Controls visibility of reject reason input
  rejectReason: string // Stores the reject reason text
  activeQuestionId: number | null // ID of question form currently being shown
  sidebarOpen: boolean // Whether sidebar is visible
  sidebarExpanded: boolean // Full-screen overlay mode
  sidebarSections: Record<string, boolean> // Which sections are expanded
  sidebarViewMode: ViewMode // Current view mode of sidebar
  sidebarScrollToEntityKey: string | undefined // Entity key to scroll to in entity_notes view
  selectedReportId: number | undefined // ID of report to display in interactive_report view
  socketConnected: boolean // Actual current socket connection state
  previouslyConnected: boolean // Whether socket has ever connected (distinguishes "connecting" from "reconnecting")
  // Ephemeral stream state stays separate from persisted events so we can render
  // websocket chunks immediately before the backend event list catches up.
  streamDrafts?: Record<string, AgentStreamDraft>
} & (NewSessionState | LoadingSessionState | ExistingSessionState | ErrorState)

type AgentStreamDraft = {
  requestId: string
  sessionId: number
  subAgentSessionId?: number
  content: string
  reasoningContent: string
  done: boolean
  pending?: boolean
  updatedAt: string
}

type FrontendPlaygroundPermissions = {
  canViewCost: boolean
  canUseEvals: boolean
  canUseAdvancedOptions: boolean
  canViewDebugPanels: boolean
  canToggleShare: boolean
  canViewAllAgents: boolean
}

type SessionConfigToggleDescriptor = {
  key: string
  label: string
  description: string
  valueType: "boolean"
  defaultValue: boolean
}

type SessionConfigSelectDescriptor = {
  key: string
  label: string
  description: string
  valueType: "select"
  defaultValue: string
  options: string[]
}

type SessionConfigUIDescriptor =
  | SessionConfigToggleDescriptor
  | SessionConfigSelectDescriptor

const RESTRICTIVE_PLAYGROUND_PERMISSIONS: FrontendPlaygroundPermissions = {
  canViewCost: false,
  canUseEvals: false,
  canUseAdvancedOptions: false,
  canViewDebugPanels: false,
  canToggleShare: false,
  canViewAllAgents: false,
}

function getAgentTypeInfo(
  agentType: string | null,
  availableAgentTypes: AgentTypeInfo[]
): AgentTypeInfo | undefined {
  if (!agentType) return undefined
  return availableAgentTypes.find((type) => type.name === agentType)
}

function toSessionConfigUIDescriptor(
  descriptor: ApiSessionConfigDescriptor
): SessionConfigUIDescriptor | null {
  if (descriptor.value_type === "boolean") {
    return {
      key: descriptor.key,
      label: descriptor.label,
      description: descriptor.description,
      valueType: "boolean",
      defaultValue: descriptor.default_value === true,
    }
  }
  if (
    descriptor.value_type === "select" &&
    descriptor.options &&
    descriptor.options.length > 0
  ) {
    return {
      key: descriptor.key,
      label: descriptor.label,
      description: descriptor.description,
      valueType: "select",
      defaultValue:
        typeof descriptor.default_value === "string"
          ? descriptor.default_value
          : descriptor.options[0],
      options: descriptor.options,
    }
  }
  return null
}

function getSessionConfigDescriptorsForAgentType(
  agentType: string | null,
  availableAgentTypes: AgentTypeInfo[]
): SessionConfigUIDescriptor[] {
  const agentTypeInfo = getAgentTypeInfo(agentType, availableAgentTypes)
  return (agentTypeInfo?.session_config_descriptors || [])
    .map(toSessionConfigUIDescriptor)
    .filter((descriptor): descriptor is SessionConfigUIDescriptor =>
      Boolean(descriptor)
    )
}

function buildSessionConfigFromDescriptors(
  descriptors: SessionConfigUIDescriptor[],
  overrides?: Record<string, boolean | string>
): EvaluationSessionConfig {
  const config: EvaluationSessionConfig = {}

  for (const descriptor of descriptors) {
    const overrideValue = overrides?.[descriptor.key]
    if (overrideValue !== undefined) {
      config[descriptor.key] = overrideValue
    } else {
      config[descriptor.key] = descriptor.defaultValue
    }
  }

  return config
}

// Resolves the string value to use as a controlled <Select> value for a select-type
// session config descriptor. Falls back to the descriptor's default when no override
// is set or the override is the wrong type (e.g. a boolean from a previous descriptor
// shape). Exported so render sites can keep Selects controlled by Redux state.
export function selectStringValue(
  override: boolean | string | undefined,
  defaultValue: string
): string {
  return typeof override === "string" ? override : defaultValue
}

// --- Evaluation Page State Types ---

type EvaluationsListState = {
  evaluations: AgentEvaluationSummary[]
  pagingMeta: PagingMeta | null
  loading: boolean
  error: string | null
  selectedTags: string[]
  selectedAgentType: string | null
  availableTags: string[]
  availableAgentTypes: AgentTypeInfo[]
  initialized: boolean
  defaultModelId: string | null
  selectedModelId: string | null
}

type EvaluationDetailState = {
  evaluation: AgentEvaluationDetail | null
  runs: AgentEvaluationRunSummary[]
  runsPagingMeta: PagingMeta | null
  loading: boolean
  runsLoading: boolean
  error: string | null
}

type EvaluationRunDetailState = {
  run: AgentEvaluationRunDetail | null
  loading: boolean
  error: string | null
  sessionEvents: AgentSessionEventDetail[]
  sessionEventsLoading: boolean
}

type EvaluationReportDetailState = {
  report: AgentEvaluationReportDetail | null
  reportId: number | null
  loading: boolean
  error: string | null
}

export interface EvaluationFormState {
  name: string
  description: string
  agentType: string
  tags: string[]
  tagsInput: string
  initialPrompt: string
  maxIterations: number | null
  passingGradeThreshold: number | null
  maxTurns: number | null
  launchTool: EvaluationLaunchTool | null
  driverObjective: string
  chatConfigBot: string
  chatConfigVertical: string
  chatConfigOrgid: string
  chatConfigTrip: EvaluationFormTrip | null
  evalKeyFacts: string[]
  evalMemories: EvaluationFormMemory[]
  preprocessInstruction: string
  followUpInstruction: string
  sessionUserId: number | null
  sessionUserLookup: EvaluationUserLookup | null
  sessionUserInput: string
  sessionUserLoading: boolean
  sessionUserError: string | null
  assertions: EvaluationFormAssertion[]
  timeTargetSeconds: number | null
  timeTargetEnabled: boolean
  saving: boolean
  error: string | null
  loadingCloneSource: boolean
  generatedFrom: "session" | "description" | null
  generationExplanation: string | null
  generationExplanationNonce: number | null
}

export type EvaluationFormPayload = CreateEvaluationPayload & {
  driver_config: EvaluationDriverConfig
}

interface GenerateEvalFormState {
  open: boolean
  sessionId: number | null
  agentType: string
  initialMessage: string
  isResultGood: "yes" | "no"
  feedback: string
  additionalNotes: string
  autoGenerateAssertions: boolean
  autoGenerateDriverObjective: boolean
  autoGeneratePreprocessInstruction: boolean
  autoGenerateTags: boolean
  autoGenerateFollowUpInstruction: boolean
  loading: boolean
  error: string | null
}

interface EvalBuilderFormState {
  open: boolean
  description: string
  mode: "create" | "refine"
  agentType: string
  autoGenerateAssertions: boolean
  autoGenerateDriverObjective: boolean
  autoGeneratePreprocessInstruction: boolean
  autoGenerateTags: boolean
  autoGenerateFollowUpInstruction: boolean
  loading: boolean
  error: string | null
}

export interface GeneratedEvalConfig {
  name: string
  description: string
  generation_explanation?: string
  agent_type: string
  initial_prompt: string
  assertions: {
    items: Array<{ assertion: string; severity: AssertionSeverity }>
  }
  driver_config: {
    max_turns?: number | null
    driver_objective?: string
    chat_config?: EvaluationChatConfig | null
    key_facts?: string[]
    memories?: EvaluationMemory[]
    launch_tool?: EvaluationLaunchTool | null
  }
  preprocess_instruction?: string
  follow_up_instruction?: string
  tags?: string[]
}

interface EvalRunDialogState {
  open: boolean
  evaluationId: number | null
  agentType: string | null
  modelId: string | null
  thinkingLevel: ThinkingOverridePreset | null
  sessionConfig: Record<string, boolean | string>
  running: boolean
}

type MultiRunSelectedRow = AgentEvaluationPickerRow

type MultiRunAllMatchingSelection = {
  filter: AgentEvaluationPickerFilter
  totalCount: number
  matchingAgentTypes: string[]
  excludedIds: number[]
}

type MultiRunPickerRequest = {
  page: number
  replace: boolean
  searchQuery: string
  filterTags: string[]
  filterAgentType: string | null
}

interface EvalMultiRunDialogState {
  open: boolean
  availableRows: AgentEvaluationPickerRow[]
  pagingMeta: PagingMeta | null
  loadingEvaluations: boolean
  loadingMoreEvaluations: boolean
  pickerRequest: MultiRunPickerRequest | null
  pickerError: string | null
  loadMorePickerError: string | null
  matchingAgentTypes: string[]
  searchQuery: string
  filterTags: string[]
  filterAgentType: string | null
  explicitRows: MultiRunSelectedRow[]
  allMatching: MultiRunAllMatchingSelection | null
  isRunning: boolean
  modelIdByType: Record<string, string>
  thinkingLevelByType: Record<string, ThinkingOverridePreset>
  overridesByType: Record<string, Record<string, boolean | string>>
}

type EvaluationsPageState = {
  list: EvaluationsListState
  detail: EvaluationDetailState
  runDetail: EvaluationRunDetailState
  reportDetail: EvaluationReportDetailState
  form: EvaluationFormState
  generateEvalForm: GenerateEvalFormState
  evalBuilderForm: EvalBuilderFormState
  runningEvaluationIds: number[]
  availableModels: AgentModelOption[]
  availableOrganizations: EvaluationOrganizationOption[]
  runDialog: EvalRunDialogState
  multiRunDialog: EvalMultiRunDialogState
}

// Shared cache for session metrics (used by both agentic playground and evaluation runs)
type SessionMetricsCacheEntry = {
  metrics: EnhancedAgentSessionMetrics | null
  loading: boolean
  error: string | null
  lastFetched: number
}

type EvaluationRunsPageState = {
  runs: AgentEvaluationRunSummary[]
  loading: boolean
  error: string | null
  pagingMeta: PagingMeta | null
  filters: {
    runIds: number[]
    evaluationId: number | null
    status: EvaluationRunStatus | null
    fromDate: string | null
    toDate: string | null
  }
}

export interface EvaluationFormTrip extends EvaluationTripConfig {
  major_stops: Array<EvaluationTripStop & { clientId: string; name: string }>
}

type EvaluationFormMemory = EvaluationMemory & {
  clientId: string
}

type EvaluationFormAssertion = EvaluationAssertion & {
  clientId: string
}

let evaluationFormClientId = 0

function nextEvaluationFormClientId(): string {
  evaluationFormClientId += 1
  return `evaluation-form-row-${evaluationFormClientId}`
}

function toEvaluationFormTrip(
  trip: EvaluationChatConfig["trip"]
): EvaluationFormTrip | null {
  if (!trip) {
    return null
  }
  return {
    ...trip,
    major_stops: trip.major_stops.map((stop) => ({
      ...stop,
      name: "",
      clientId: nextEvaluationFormClientId(),
    })),
  }
}

function toEvaluationFormMemory(
  memory: EvaluationMemory
): EvaluationFormMemory {
  return {
    ...memory,
    clientId: nextEvaluationFormClientId(),
  }
}

function toEvaluationFormAssertion(
  assertion: EvaluationAssertion
): EvaluationFormAssertion {
  return {
    ...assertion,
    clientId: nextEvaluationFormClientId(),
  }
}

function stripEvaluationMemoryClientId(
  memory: EvaluationFormMemory
): EvaluationMemory {
  return {
    path: memory.path,
    key: memory.key,
    value: memory.value,
  }
}

function stripEvaluationAssertionClientId(
  assertion: EvaluationFormAssertion
): EvaluationAssertion {
  return {
    assertion: assertion.assertion,
    severity: assertion.severity,
  }
}

type ChatTranscriptState = {
  transcript: ChatTranscriptResponse | null
  loading: boolean
  error: string | null
}

export interface InternalAgenticSliceState {
  agenticDetailPage: AgenticDetailPageState
  // set on the organization dashboard: its agent calls go to that org's routes
  agenticDetailOrgid: string | null
  playgroundPermissions: PlaygroundPermissions | null
  // Flight options cache keyed by flight search result ID
  flightOptionsCache: Record<number, FlightOptionCacheEntry>
  // Flight quote cache keyed by quote ref (e.g. "fq-A62LopFe")
  flightQuotesCache: Record<string, FlightQuoteCacheEntry>
  // Individual flights cache keyed by flight ID (from flight_legs)
  flightsCache: Record<number, FlightLegApi>
  evaluationsPage: EvaluationsPageState
  evaluationRunsPage: EvaluationRunsPageState
  chatTranscript: ChatTranscriptState
  // Shared session metrics cache (keyed by session ID)
  sessionMetricsCache: Record<number, SessionMetricsCacheEntry>
}

const emptyEvaluationRunsPageState = (): EvaluationRunsPageState => ({
  runs: [],
  loading: true,
  error: null,
  pagingMeta: null,
  filters: {
    runIds: [],
    evaluationId: null,
    status: null,
    fromDate: null,
    toDate: null,
  },
})

const emptyChatTranscriptState = (): ChatTranscriptState => ({
  transcript: null,
  loading: false,
  error: null,
})

const emptyEvalMultiRunDialogState = (
  open: boolean,
  loadingEvaluations: boolean
): EvalMultiRunDialogState => ({
  open,
  availableRows: [],
  pagingMeta: null,
  loadingEvaluations,
  loadingMoreEvaluations: false,
  pickerRequest: null,
  pickerError: null,
  loadMorePickerError: null,
  matchingAgentTypes: [],
  searchQuery: "",
  filterTags: [],
  filterAgentType: null,
  explicitRows: [],
  allMatching: null,
  isRunning: false,
  modelIdByType: {},
  thinkingLevelByType: {},
  overridesByType: {},
})

const currentMultiRunFilter = (
  dialog: EvalMultiRunDialogState
): AgentEvaluationPickerFilter => ({
  q: dialog.searchQuery,
  tags: [...dialog.filterTags],
  agent_type: dialog.filterAgentType,
})

const stringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index])

const multiRunPickerRequestMatches = (
  dialog: EvalMultiRunDialogState,
  request: MultiRunPickerRequest
) => {
  const activeRequest = dialog.pickerRequest
  if (activeRequest === null) return false

  const requestMatchesActive =
    activeRequest.page === request.page &&
    activeRequest.replace === request.replace &&
    activeRequest.searchQuery === request.searchQuery &&
    activeRequest.filterAgentType === request.filterAgentType &&
    stringArraysEqual(activeRequest.filterTags, request.filterTags)
  const requestMatchesCurrentFilter =
    dialog.searchQuery === request.searchQuery &&
    dialog.filterAgentType === request.filterAgentType &&
    stringArraysEqual(dialog.filterTags, request.filterTags)
  const requestMatchesAppendPosition =
    request.replace || dialog.pagingMeta?.current_page === request.page - 1

  return (
    requestMatchesActive &&
    requestMatchesCurrentFilter &&
    requestMatchesAppendPosition
  )
}

export function pickerRowMatchesFilter(
  row: AgentEvaluationPickerRow,
  filter: AgentEvaluationPickerFilter
): boolean {
  const matchesSearch =
    filter.q.trim() === "" ||
    row.name.toLowerCase().includes(filter.q.trim().toLowerCase())
  const matchesTags =
    filter.tags.length === 0 ||
    filter.tags.some((tag) => row.tags.includes(tag))
  const matchesAgentType =
    filter.agent_type === null || row.agent_type === filter.agent_type

  return matchesSearch && matchesTags && matchesAgentType
}

const multiRunRowIsSelected = (
  dialog: EvalMultiRunDialogState,
  row: AgentEvaluationPickerRow
): boolean => {
  const explicitlySelected = dialog.explicitRows.some((r) => r.id === row.id)
  const allMatchingSelected =
    dialog.allMatching !== null &&
    pickerRowMatchesFilter(row, dialog.allMatching.filter) &&
    !dialog.allMatching.excludedIds.includes(row.id)

  return explicitlySelected || allMatchingSelected
}

const emptyEvaluationsPageState = (): EvaluationsPageState => ({
  list: {
    evaluations: [],
    pagingMeta: null,
    loading: true,
    error: null,
    selectedTags: [],
    selectedAgentType: null,
    availableTags: [],
    availableAgentTypes: [],
    initialized: false,
    defaultModelId: null,
    selectedModelId: null,
  },
  detail: {
    evaluation: null,
    runs: [],
    runsPagingMeta: null,
    loading: false,
    runsLoading: false,
    error: null,
  },
  runDetail: {
    run: null,
    loading: false,
    error: null,
    sessionEvents: [],
    sessionEventsLoading: false,
  },
  reportDetail: {
    report: null,
    reportId: null,
    loading: false,
    error: null,
  },
  form: {
    name: "",
    description: "",
    agentType: "Agent::LeadAgent",
    tags: [],
    tagsInput: "",
    initialPrompt: "",
    maxIterations: null,
    passingGradeThreshold: null,
    maxTurns: null,
    launchTool: null,
    driverObjective: "",
    chatConfigBot: "",
    chatConfigVertical: "",
    chatConfigOrgid: "",
    chatConfigTrip: null,
    evalKeyFacts: [],
    evalMemories: [],
    preprocessInstruction: "",
    followUpInstruction: "",
    sessionUserId: null,
    sessionUserLookup: null,
    sessionUserInput: "",
    sessionUserLoading: false,
    sessionUserError: null,
    assertions: [
      {
        assertion: "",
        severity: "normal",
        clientId: nextEvaluationFormClientId(),
      },
    ],
    timeTargetSeconds: null,
    timeTargetEnabled: false,
    saving: false,
    error: null,
    loadingCloneSource: false,
    generatedFrom: null,
    generationExplanation: null,
    generationExplanationNonce: null,
  },
  generateEvalForm: {
    open: false,
    sessionId: null,
    agentType: "Agent::LeadAgent",
    initialMessage: "",
    isResultGood: "yes",
    feedback: "",
    additionalNotes: "",
    autoGenerateAssertions: true,
    autoGenerateDriverObjective: true,
    autoGeneratePreprocessInstruction: true,
    autoGenerateTags: true,
    autoGenerateFollowUpInstruction: false,
    loading: false,
    error: null,
  },
  evalBuilderForm: {
    open: false,
    description: "",
    mode: "create",
    agentType: "Agent::LeadAgent",
    autoGenerateAssertions: true,
    autoGenerateDriverObjective: true,
    autoGeneratePreprocessInstruction: true,
    autoGenerateTags: true,
    autoGenerateFollowUpInstruction: false,
    loading: false,
    error: null,
  },
  runningEvaluationIds: [],
  availableModels: [],
  availableOrganizations: [],
  runDialog: {
    open: false,
    evaluationId: null,
    agentType: null,
    modelId: null,
    thinkingLevel: null,
    sessionConfig: {},
    running: false,
  },
  multiRunDialog: emptyEvalMultiRunDialogState(false, false),
})

export const internalAgenticSliceInitialState: InternalAgenticSliceState = {
  agenticDetailPage: {
    loading: true,
    type: "new",
    sessionForm: emptySessionForm(),
    metrics: emptyMetrics(),
    enhancedMetrics: null,
    metricsLoading: false,
    metricsError: null,
    metricsExpanded: false,
    sendingMessage: false,
    stoppingSession: false,
    messageError: null,
    availableAgentTypes: [],
    expandedContentStates: {},
    showRejectInput: false,
    rejectReason: "",
    activeQuestionId: null,
    sidebarOpen: false,
    sidebarExpanded: false,
    sidebarSections: { overview: true },
    sidebarViewMode: "inline",
    sidebarScrollToEntityKey: undefined,
    selectedReportId: undefined,
    socketConnected: false,
    previouslyConnected: false,
    streamDrafts: {},
  },
  playgroundPermissions: null,
  flightOptionsCache: {},
  flightQuotesCache: {},
  flightsCache: {},
  evaluationsPage: emptyEvaluationsPageState(),
  evaluationRunsPage: emptyEvaluationRunsPageState(),
  chatTranscript: emptyChatTranscriptState(),
  sessionMetricsCache: {},
  agenticDetailOrgid: null,
}

// Shared helper for applying a single stream chunk to the Immer draft.
// Used by both applyAgenticStreamChunk (single) and
// applyAgenticStreamChunkBatch (batched rAF flush).
function applyStreamChunkToDraft(
  state: InternalAgenticSliceState,
  chunk: AgentStreamChunkEventData
) {
  const key = `${chunk.session_id}:${chunk.sub_agent_session_id ?? "root"}:${chunk.request_id}`
  const streamDrafts = state.agenticDetailPage.streamDrafts || {}

  // Only rebuild the drafts object when there are pending placeholder
  // drafts to remove. The previous implementation always created a new
  // object via Object.fromEntries(), which forced Immer to produce new
  // state even when nothing was filtered out.
  const hasPendingToRemove = Object.values(streamDrafts).some(
    (draft) =>
      draft.pending &&
      draft.sessionId === chunk.session_id &&
      (draft.subAgentSessionId ?? null) === (chunk.sub_agent_session_id ?? null)
  )
  if (hasPendingToRemove) {
    state.agenticDetailPage.streamDrafts = Object.fromEntries(
      Object.entries(streamDrafts).filter(
        ([, draft]) =>
          !(
            draft.pending &&
            draft.sessionId === chunk.session_id &&
            (draft.subAgentSessionId ?? null) ===
              (chunk.sub_agent_session_id ?? null)
          )
      )
    )
  }

  const currentDrafts = state.agenticDetailPage.streamDrafts || {}

  if (!currentDrafts[key]) {
    currentDrafts[key] = {
      requestId: chunk.request_id,
      sessionId: chunk.session_id,
      subAgentSessionId: chunk.sub_agent_session_id,
      content: "",
      reasoningContent: "",
      done: false,
      pending: false,
      updatedAt: chunk.timestamp,
    }
  }

  const draft = currentDrafts[key]
  if (chunk.chunk_type === "text" && chunk.text_delta) {
    draft.content += chunk.text_delta
  } else if (chunk.chunk_type === "reasoning" && chunk.text_delta) {
    draft.reasoningContent += chunk.text_delta
  } else if (chunk.chunk_type === "done" || chunk.chunk_type === "error") {
    draft.done = true
  }

  draft.updatedAt = chunk.timestamp
  state.agenticDetailPage.streamDrafts = currentDrafts
}

const internalAgenticSlice = createSlice({
  name: "internalAgenticSlice",
  initialState: internalAgenticSliceInitialState,
  reducers: {
    setAgenticDetailPage: (
      state,
      action: PayloadAction<AgenticDetailPageState>
    ) => {
      state.agenticDetailPage = {
        ...action.payload,
        streamDrafts:
          action.payload.streamDrafts ??
          state.agenticDetailPage.streamDrafts ??
          {},
      }
    },
    setAgenticDetailSession: (
      state,
      action: PayloadAction<AgentSessionDetail>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.session = action.payload
        // a session that stopped processing without a final stream chunk would otherwise say "Thinking…" forever
        if (
          action.payload.status !== "pending" &&
          action.payload.status !== "working"
        ) {
          const streamDrafts = state.agenticDetailPage.streamDrafts || {}
          state.agenticDetailPage.streamDrafts = Object.fromEntries(
            Object.entries(streamDrafts).filter(
              ([, draft]) =>
                !(
                  draft.pending &&
                  draft.sessionId === action.payload.id &&
                  draft.subAgentSessionId == null
                )
            )
          )
        }
      }
    },
    setAgenticDetailSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.agenticDetailPage.sendingMessage = action.payload
    },
    setPlaygroundPermissions: (
      state,
      action: PayloadAction<PlaygroundPermissions>
    ) => {
      state.playgroundPermissions = action.payload
    },
    setAgenticDetailOrgid: (state, action: PayloadAction<string | null>) => {
      state.agenticDetailOrgid = action.payload
    },
    setAgenticDetailStoppingSession: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.agenticDetailPage.stoppingSession = action.payload
    },
    setAgenticDetailLoading: (state, action: PayloadAction<boolean>) => {
      state.agenticDetailPage.loading = action.payload
    },
    setAgenticDetailMetricsExpanded: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.agenticDetailPage.metricsExpanded = action.payload
    },
    setAgenticDetailMessageError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.agenticDetailPage.messageError = action.payload
    },
    setAgenticDetailSelectedAgentType: (
      state,
      action: PayloadAction<string | null>
    ) => {
      const selectedAgentType = action.payload
      state.agenticDetailPage.sessionForm.selectedAgentType = selectedAgentType
      if (state.agenticDetailPage.type === "new") {
        // On new sessions, selecting an agent type should always snap to that
        // agent type's backend-provided default model, even if previous model
        // remains technically allowed.
        state.agenticDetailPage.sessionForm.selectedModelId =
          defaultModelIdForAgentType(
            state.agenticDetailPage.sessionForm.availableModels,
            selectedAgentType,
            state.agenticDetailPage.availableAgentTypes
          )
        return
      }

      const allowedModels = filterAllowedModelsForAgentType(
        state.agenticDetailPage.sessionForm.availableModels,
        selectedAgentType,
        state.agenticDetailPage.availableAgentTypes
      )
      const selectedModelId =
        state.agenticDetailPage.sessionForm.selectedModelId
      const selectedModelStillAllowed =
        selectedModelId !== null &&
        allowedModels.some((model) => model.id === selectedModelId)

      if (!selectedModelStillAllowed) {
        state.agenticDetailPage.sessionForm.selectedModelId =
          defaultModelIdForAgentType(
            state.agenticDetailPage.sessionForm.availableModels,
            selectedAgentType,
            state.agenticDetailPage.availableAgentTypes
          )
      }
    },
    setAgenticDetailSelectedModelId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.agenticDetailPage.sessionForm.selectedModelId = action.payload
    },
    setAgenticDetailAvailableModels: (
      state,
      action: PayloadAction<AgentModelOption[]>
    ) => {
      state.agenticDetailPage.sessionForm.availableModels = action.payload
      const allowedModels = filterAllowedModelsForAgentType(
        action.payload,
        state.agenticDetailPage.sessionForm.selectedAgentType,
        state.agenticDetailPage.availableAgentTypes
      )
      const selectedModelId =
        state.agenticDetailPage.sessionForm.selectedModelId
      const selectedModelStillAllowed =
        selectedModelId !== null &&
        allowedModels.some((model) => model.id === selectedModelId)

      if (!selectedModelStillAllowed) {
        state.agenticDetailPage.sessionForm.selectedModelId =
          defaultModelIdForAgentType(
            action.payload,
            state.agenticDetailPage.sessionForm.selectedAgentType,
            state.agenticDetailPage.availableAgentTypes
          )
      }
    },
    applyAgenticStreamChunk: (
      state,
      action: PayloadAction<AgentStreamChunkEventData>
    ) => {
      applyStreamChunkToDraft(state, action.payload)
    },
    // Processes multiple stream chunks in a single reducer pass. This is
    // used by the rAF-buffered socket handler to avoid one dispatch (and
    // one forced synchronous re-render) per chunk. All chunks mutate the
    // same Immer draft, so React sees exactly one state transition.
    applyAgenticStreamChunkBatch: (
      state,
      action: PayloadAction<AgentStreamChunkEventData[]>
    ) => {
      for (const chunk of action.payload) {
        applyStreamChunkToDraft(state, chunk)
      }
    },
    startPendingAgenticStreamDraft: (
      state,
      action: PayloadAction<{ sessionId: number; subAgentSessionId?: number }>
    ) => {
      const { sessionId, subAgentSessionId } = action.payload
      const key = `${sessionId}:${subAgentSessionId ?? "root"}:pending`
      const streamDrafts = state.agenticDetailPage.streamDrafts || {}
      streamDrafts[key] = {
        requestId: "pending",
        sessionId,
        subAgentSessionId,
        content: "",
        reasoningContent: "",
        done: false,
        pending: true,
        updatedAt: nowISOString(),
      }
      state.agenticDetailPage.streamDrafts = streamDrafts
    },
    clearPendingAgenticStreamDraft: (
      state,
      action: PayloadAction<{ sessionId: number; subAgentSessionId?: number }>
    ) => {
      const { sessionId, subAgentSessionId } = action.payload
      const streamDrafts = state.agenticDetailPage.streamDrafts || {}
      state.agenticDetailPage.streamDrafts = Object.fromEntries(
        Object.entries(streamDrafts).filter(
          ([, draft]) =>
            !(
              draft.pending &&
              draft.sessionId === sessionId &&
              (draft.subAgentSessionId ?? null) === (subAgentSessionId ?? null)
            )
        )
      )
    },
    clearCompletedAgenticStreamDrafts: (state) => {
      const streamDrafts = state.agenticDetailPage.streamDrafts || {}
      state.agenticDetailPage.streamDrafts = Object.fromEntries(
        Object.entries(streamDrafts).filter(([, draft]) => !draft.done)
      )
    },
    setAgenticDetailMaxIterations: (state, action: PayloadAction<number>) => {
      state.agenticDetailPage.sessionForm.maxIterations = action.payload
    },
    setAgenticDetailMaxIterationsFromInput: (
      state,
      action: PayloadAction<string>
    ) => {
      const value = action.payload

      if (value === "") {
        // Reset to default value if cleared
        state.agenticDetailPage.sessionForm.maxIterations = 100
        return
      }

      const parsed = Number.parseInt(value, 10)

      if (Number.isNaN(parsed) || parsed < 1) {
        // Keep current value if invalid input
        return
      }

      state.agenticDetailPage.sessionForm.maxIterations = parsed
    },
    setAgenticDetailThinkingLevel: (
      state,
      action: PayloadAction<ThinkingLevel>
    ) => {
      state.agenticDetailPage.sessionForm.thinkingLevel = action.payload
    },
    setAgenticDetailShared: (state, action: PayloadAction<boolean>) => {
      state.agenticDetailPage.sessionForm.shared = action.payload
    },
    setAgenticDetailSessionConfigField: (
      state,
      action: PayloadAction<{ key: string; value: boolean | string }>
    ) => {
      state.agenticDetailPage.sessionForm.sessionConfigOverrides[
        action.payload.key
      ] = action.payload.value
    },
    setAgenticDetailAvailableAgentTypes: (
      state,
      action: PayloadAction<AgentTypeInfo[]>
    ) => {
      state.agenticDetailPage.availableAgentTypes = action.payload
    },
    setAgenticDetailEvents: (
      state,
      action: PayloadAction<AgentSessionEventDetail[]>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.events = action.payload
        state.agenticDetailPage.eventsLoading = false
        const streamDrafts = state.agenticDetailPage.streamDrafts || {}
        state.agenticDetailPage.streamDrafts = Object.fromEntries(
          Object.entries(streamDrafts).filter(([, draft]) => !draft.done)
        )
      }
    },
    appendAgenticDetailEvents: (
      state,
      action: PayloadAction<AgentSessionEventDetail[]>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.events = [
          ...state.agenticDetailPage.events,
          ...action.payload,
        ]
      }
    },
    prependAgenticDetailEvents: (
      state,
      action: PayloadAction<AgentSessionEventDetail[]>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.events = [
          ...action.payload,
          ...state.agenticDetailPage.events,
        ]
      }
    },
    setAgenticDetailEventsLoading: (state, action: PayloadAction<boolean>) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.eventsLoading = action.payload
      }
    },
    setAgenticDetailHasMoreEvents: (state, action: PayloadAction<boolean>) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.hasMoreEvents = action.payload
      }
    },
    setAgenticDetailStatus: (
      state,
      action: PayloadAction<AgentSessionStatus>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.session.status = action.payload
      }
    },
    setAgenticDetailTitle: (state, action: PayloadAction<string>) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.session.title = action.payload
      }
    },
    setAgenticDetailCurrentMode: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.session.current_mode = action.payload
      }
    },
    setAgenticDetailStatusMessage: (
      state,
      action: PayloadAction<string | null>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        if (state.agenticDetailPage.statusUpdate) {
          state.agenticDetailPage.statusUpdate.status_message =
            action.payload ?? ""
        }
        state.agenticDetailPage.statusMessage = action.payload
      }
    },
    setAgenticDetailStatusUpdate: (
      state,
      action: PayloadAction<AgentStatusUpdateEventData | null>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.statusUpdate = action.payload
        state.agenticDetailPage.statusMessage =
          action.payload?.status_message ?? null
      }
    },
    setAgenticDetailPendingPlan: (
      state,
      action: PayloadAction<AgentPlanEventDetail | undefined>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.session.pending_plan = action.payload
      }
    },
    setAgenticDetailMetrics: (
      state,
      action: PayloadAction<AgentSessionMetrics>
    ) => {
      state.agenticDetailPage.metrics = action.payload
    },
    toggleAgenticContentExpanded: (state, action: PayloadAction<string>) => {
      const key = action.payload
      state.agenticDetailPage.expandedContentStates[key] =
        !state.agenticDetailPage.expandedContentStates[key]
    },
    setAgenticDetailShowRejectInput: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.agenticDetailPage.showRejectInput = action.payload
    },
    setAgenticDetailRejectReason: (state, action: PayloadAction<string>) => {
      state.agenticDetailPage.rejectReason = action.payload
    },
    resetAgenticDetailRejectState: (state) => {
      state.agenticDetailPage.showRejectInput = false
      state.agenticDetailPage.rejectReason = ""
    },
    setAgenticDetailActiveQuestionId: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.agenticDetailPage.activeQuestionId = action.payload
    },
    updateAgenticDetailQuestionEventStatus: (
      state,
      action: PayloadAction<{
        questionId: number
        status: "answered" | "skipped"
        answers?: Answers
      }>
    ) => {
      if (state.agenticDetailPage.type === "existing") {
        const { questionId, status, answers } = action.payload
        const event = state.agenticDetailPage.events.find(
          (e) => e.id === questionId
        )
        if (event && isAgentQuestionEvent(event)) {
          event.status = status
          if (answers) {
            event.answers = answers
          }
        }
      }
    },
    setAgenticDetailSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.agenticDetailPage.sidebarOpen = action.payload
    },
    setAgenticDetailSidebarExpanded: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.agenticDetailPage.sidebarExpanded = action.payload
    },
    toggleAgenticDetailSidebarSection: (
      state,
      action: PayloadAction<string>
    ) => {
      const key = action.payload
      state.agenticDetailPage.sidebarSections[key] =
        !state.agenticDetailPage.sidebarSections[key]
    },
    setAgenticDetailSidebarViewMode: (
      state,
      action: PayloadAction<ViewMode>
    ) => {
      state.agenticDetailPage.sidebarViewMode = action.payload
    },
    setAgenticDetailSidebarScrollToEntityKey: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.agenticDetailPage.sidebarScrollToEntityKey = action.payload
    },
    setAgenticDetailSelectedReportId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.agenticDetailPage.selectedReportId = action.payload
    },
    setSubAgentMetricsLoading: (
      state,
      action: PayloadAction<{ sessionId: number; loading: boolean }>
    ) => {
      const { sessionId, loading } = action.payload

      if (!state.sessionMetricsCache[sessionId]) {
        state.sessionMetricsCache[sessionId] = {
          metrics: null,
          loading,
          error: null,
          lastFetched: 0,
        }
      } else {
        state.sessionMetricsCache[sessionId].loading = loading
      }
    },
    setSubAgentMetrics: (
      state,
      action: PayloadAction<{
        sessionId: number
        metrics: EnhancedAgentSessionMetrics
      }>
    ) => {
      const { sessionId, metrics } = action.payload

      // Evict oldest entry if cache exceeds max size
      const MAX_CACHE_SIZE = 50
      const cacheKeys = Object.keys(state.sessionMetricsCache)
      if (cacheKeys.length >= MAX_CACHE_SIZE) {
        let oldestKey = cacheKeys[0]
        let oldestTime =
          state.sessionMetricsCache[Number(oldestKey)]?.lastFetched ?? Infinity
        for (const key of cacheKeys) {
          const entry = state.sessionMetricsCache[Number(key)]
          if (entry && entry.lastFetched < oldestTime) {
            oldestTime = entry.lastFetched
            oldestKey = key
          }
        }
        // Rebuild cache without oldest entry (avoids dynamic delete lint error)
        const oldestKeyNum = Number(oldestKey)
        state.sessionMetricsCache = Object.fromEntries(
          Object.entries(state.sessionMetricsCache).filter(
            ([k]) => Number(k) !== oldestKeyNum
          )
        )
      }

      state.sessionMetricsCache[sessionId] = {
        metrics,
        loading: false,
        error: null,
        lastFetched: nowMillis(),
      }
    },
    setSubAgentMetricsError: (
      state,
      action: PayloadAction<{ sessionId: number; error: string }>
    ) => {
      const { sessionId, error } = action.payload

      if (!state.sessionMetricsCache[sessionId]) {
        state.sessionMetricsCache[sessionId] = {
          metrics: null,
          loading: false,
          error,
          lastFetched: 0,
        }
      } else {
        state.sessionMetricsCache[sessionId].loading = false
        state.sessionMetricsCache[sessionId].error = error
      }
    },
    setAgenticDetailSessionShared: (state, action: PayloadAction<boolean>) => {
      if (state.agenticDetailPage.type === "existing") {
        state.agenticDetailPage.session.shared = action.payload
      }
    },
    setAgenticDetailEnhancedMetrics: (
      state,
      action: PayloadAction<EnhancedAgentSessionMetrics | null>
    ) => {
      state.agenticDetailPage.enhancedMetrics = action.payload
    },
    setAgenticDetailMetricsLoading: (state, action: PayloadAction<boolean>) => {
      state.agenticDetailPage.metricsLoading = action.payload
    },
    setAgenticDetailMetricsError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.agenticDetailPage.metricsError = action.payload
    },
    setAgenticDetailSocketConnected: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.agenticDetailPage.socketConnected = action.payload
    },
    setAgenticDetailPreviouslyConnected: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.agenticDetailPage.previouslyConnected = action.payload
    },
    // Flight options cache reducers (batch only)
    setFlightOptionsLoading: (state, action: PayloadAction<number[]>) => {
      for (const id of action.payload) {
        if (!state.flightOptionsCache[id]) {
          state.flightOptionsCache[id] = {
            data: null,
            loading: true,
            error: null,
          }
        } else {
          state.flightOptionsCache[id].loading = true
        }
      }
    },
    setFlightOption: (
      state,
      action: PayloadAction<{
        id: number
        data: FlightOptionWithMeta
      }>
    ) => {
      const { id, data } = action.payload
      state.flightOptionsCache[id] = { data, loading: false, error: null }
    },
    setFlightOptionsError: (
      state,
      action: PayloadAction<{ ids: number[]; error: string }>
    ) => {
      const { ids, error } = action.payload
      for (const id of ids) {
        if (!state.flightOptionsCache[id]) {
          state.flightOptionsCache[id] = { data: null, loading: false, error }
        } else {
          state.flightOptionsCache[id].loading = false
          state.flightOptionsCache[id].error = error
        }
      }
    },
    clearFlightOptionsCache: (state) => {
      state.flightOptionsCache = {}
    },
    // Flight quote cache reducers (batch only)
    setFlightQuotesLoading: (state, action: PayloadAction<string[]>) => {
      for (const ref of action.payload) {
        if (!state.flightQuotesCache[ref]) {
          state.flightQuotesCache[ref] = {
            data: null,
            loading: true,
            error: null,
          }
        } else {
          state.flightQuotesCache[ref].loading = true
        }
      }
    },
    setFlightQuote: (
      state,
      action: PayloadAction<{
        ref: string
        data: FlightQuoteDetails
      }>
    ) => {
      const { ref, data } = action.payload
      state.flightQuotesCache[ref] = { data, loading: false, error: null }
    },
    setFlightQuotesError: (
      state,
      action: PayloadAction<{ refs: string[]; error: string }>
    ) => {
      const { refs, error } = action.payload
      for (const ref of refs) {
        if (!state.flightQuotesCache[ref]) {
          state.flightQuotesCache[ref] = { data: null, loading: false, error }
        } else {
          state.flightQuotesCache[ref].loading = false
          state.flightQuotesCache[ref].error = error
        }
      }
    },
    clearFlightQuotesCache: (state) => {
      state.flightQuotesCache = {}
    },
    // Individual flights cache reducers
    setFlights: (state, action: PayloadAction<FlightLegApi[]>) => {
      for (const flight of action.payload) {
        if (flight.id) {
          state.flightsCache[flight.id] = flight
        }
      }
    },
    clearFlightsCache: (state) => {
      state.flightsCache = {}
    },

    // --- Evaluation Reducers ---

    setEvaluationsListLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.list.loading = action.payload
    },
    setEvaluationsListError: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.list.error = action.payload
    },
    setEvaluationsList: (
      state,
      action: PayloadAction<{
        evaluations: AgentEvaluationSummary[]
        pagingMeta: PagingMeta
      }>
    ) => {
      state.evaluationsPage.list.evaluations = action.payload.evaluations
      state.evaluationsPage.list.pagingMeta = action.payload.pagingMeta
      state.evaluationsPage.list.loading = false
      state.evaluationsPage.list.error = null
    },
    updateEvaluationInList: (
      state,
      action: PayloadAction<AgentEvaluationSummary>
    ) => {
      const index = state.evaluationsPage.list.evaluations.findIndex(
        (e) => e.id === action.payload.id
      )
      if (index !== -1) {
        state.evaluationsPage.list.evaluations[index] = action.payload
      }
    },
    removeEvaluationFromList: (state, action: PayloadAction<number>) => {
      state.evaluationsPage.list.evaluations =
        state.evaluationsPage.list.evaluations.filter(
          (e) => e.id !== action.payload
        )
    },

    setEvaluationDetailLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.detail.loading = action.payload
    },
    setEvaluationDetailError: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.detail.error = action.payload
    },
    setEvaluationDetail: (
      state,
      action: PayloadAction<AgentEvaluationDetail | null>
    ) => {
      state.evaluationsPage.detail.evaluation = action.payload
      state.evaluationsPage.detail.loading = false
      state.evaluationsPage.detail.error = null
    },
    clearEvaluationDetail: (state) => {
      state.evaluationsPage.detail.evaluation = null
      state.evaluationsPage.detail.runs = []
      state.evaluationsPage.detail.runsPagingMeta = null
      state.evaluationsPage.detail.error = null
    },

    setEvaluationRunsLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.detail.runsLoading = action.payload
    },
    setEvaluationRuns: (
      state,
      action: PayloadAction<{
        runs: AgentEvaluationRunSummary[]
        pagingMeta: PagingMeta
      }>
    ) => {
      state.evaluationsPage.detail.runs = action.payload.runs
      state.evaluationsPage.detail.runsPagingMeta = action.payload.pagingMeta
      state.evaluationsPage.detail.runsLoading = false
    },

    setEvaluationRunDetailLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.runDetail.loading = action.payload
    },
    setEvaluationRunDetailError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.evaluationsPage.runDetail.error = action.payload
    },
    setEvaluationRunDetail: (
      state,
      action: PayloadAction<AgentEvaluationRunDetail | null>
    ) => {
      state.evaluationsPage.runDetail.run = action.payload
      state.evaluationsPage.runDetail.loading = false
      state.evaluationsPage.runDetail.error = null
    },
    clearEvaluationRunDetail: (state) => {
      state.evaluationsPage.runDetail.run = null
      state.evaluationsPage.runDetail.error = null
      state.evaluationsPage.runDetail.sessionEvents = []
      state.evaluationsPage.runDetail.sessionEventsLoading = false
    },
    setEvaluationReportDetailLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.evaluationsPage.reportDetail.loading = action.payload
    },
    setEvaluationReportDetailError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.evaluationsPage.reportDetail.error = action.payload
      if (action.payload !== null) {
        state.evaluationsPage.reportDetail.loading = false
      }
    },
    setEvaluationReportDetail: (
      state,
      action: PayloadAction<{
        reportId: number
        report: AgentEvaluationReportDetail
      }>
    ) => {
      state.evaluationsPage.reportDetail.reportId = action.payload.reportId
      state.evaluationsPage.reportDetail.report = action.payload.report
      state.evaluationsPage.reportDetail.loading = false
      state.evaluationsPage.reportDetail.error = null
    },
    clearEvaluationReportDetail: (state) => {
      state.evaluationsPage.reportDetail.report = null
      state.evaluationsPage.reportDetail.reportId = null
      state.evaluationsPage.reportDetail.error = null
    },
    setEvaluationRunSessionEvents: (
      state,
      action: PayloadAction<AgentSessionEventDetail[]>
    ) => {
      state.evaluationsPage.runDetail.sessionEvents = action.payload
      state.evaluationsPage.runDetail.sessionEventsLoading = false
    },
    appendEvaluationRunSessionEvents: (
      state,
      action: PayloadAction<AgentSessionEventDetail[]>
    ) => {
      if (action.payload.length > 0) {
        state.evaluationsPage.runDetail.sessionEvents = [
          ...state.evaluationsPage.runDetail.sessionEvents,
          ...action.payload,
        ]
      }
      state.evaluationsPage.runDetail.sessionEventsLoading = false
    },
    setEvaluationRunSessionEventsLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.evaluationsPage.runDetail.sessionEventsLoading = action.payload
    },
    setChatTranscriptLoading: (state, action: PayloadAction<boolean>) => {
      state.chatTranscript.loading = action.payload
    },
    setChatTranscriptError: (state, action: PayloadAction<string | null>) => {
      state.chatTranscript.error = action.payload
    },
    setChatTranscript: (
      state,
      action: PayloadAction<ChatTranscriptResponse | null>
    ) => {
      state.chatTranscript.transcript = action.payload
      state.chatTranscript.loading = false
      state.chatTranscript.error = null
    },
    clearChatTranscript: (state) => {
      state.chatTranscript = emptyChatTranscriptState()
    },

    setEvaluationFormSaving: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.form.saving = action.payload
    },
    setEvaluationFormError: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.form.error = action.payload
    },
    setEvaluationFormLoadingClone: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.form.loadingCloneSource = action.payload
    },
    clearEvaluationFormState: (state) => {
      state.evaluationsPage.form.saving = false
      state.evaluationsPage.form.error = null
    },

    addRunningEvaluationId: (state, action: PayloadAction<number>) => {
      if (
        !state.evaluationsPage.runningEvaluationIds.includes(action.payload)
      ) {
        state.evaluationsPage.runningEvaluationIds.push(action.payload)
      }
    },
    removeRunningEvaluationId: (state, action: PayloadAction<number>) => {
      state.evaluationsPage.runningEvaluationIds =
        state.evaluationsPage.runningEvaluationIds.filter(
          (id) => id !== action.payload
        )
    },

    resetEvaluationsPage: (state) => {
      state.evaluationsPage = emptyEvaluationsPageState()
    },

    // Evaluation Runs Page actions
    setAllEvaluationRuns: (
      state,
      action: PayloadAction<{
        runs: AgentEvaluationRunSummary[]
        pagingMeta: PagingMeta
      }>
    ) => {
      state.evaluationRunsPage.runs = action.payload.runs
      state.evaluationRunsPage.pagingMeta = action.payload.pagingMeta
      state.evaluationRunsPage.loading = false
    },

    setAllEvaluationRunsLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationRunsPage.loading = action.payload
    },

    setAllEvaluationRunsError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.evaluationRunsPage.error = action.payload
      if (action.payload !== null) {
        state.evaluationRunsPage.loading = false
      }
    },

    setEvaluationRunsFilters: (
      state,
      action: PayloadAction<EvaluationRunsPageState["filters"]>
    ) => {
      state.evaluationRunsPage.filters = action.payload
    },

    setEvaluationRunsFilter: <
      K extends keyof EvaluationRunsPageState["filters"],
    >(
      state: InternalAgenticSliceState,
      action: PayloadAction<{
        field: K
        value: EvaluationRunsPageState["filters"][K]
      }>
    ) => {
      state.evaluationRunsPage.filters[action.payload.field] =
        action.payload.value
    },

    clearEvaluationRunsFilters: (state) => {
      state.evaluationRunsPage.filters = {
        runIds: [],
        evaluationId: null,
        status: null,
        fromDate: null,
        toDate: null,
      }
    },

    setEvaluationFormField: <K extends keyof EvaluationFormState>(
      state: InternalAgenticSliceState,
      action: PayloadAction<{ field: K; value: EvaluationFormState[K] }>
    ) => {
      const { field, value } = action.payload
      state.evaluationsPage.form[field] = value
      if (
        field === "agentType" &&
        value === "Agent::MiniAgent" &&
        state.evaluationsPage.form.timeTargetSeconds === null
      ) {
        state.evaluationsPage.form.timeTargetSeconds = 120
      }
      if (field === "agentType" && value !== "Agent::MiniAgent") {
        state.evaluationsPage.form.launchTool = null
      }
    },

    setEvaluationFormTags: (state, action: PayloadAction<string>) => {
      const input = action.payload
      state.evaluationsPage.form.tagsInput = input
      state.evaluationsPage.form.tags = input
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
    },

    addEvaluationFormTripStop: (
      state,
      action: PayloadAction<{ entity_ref: string; name: string }>
    ) => {
      const form = state.evaluationsPage.form
      form.chatConfigTrip ??= { major_stops: [] }
      form.chatConfigTrip.major_stops.push({
        ...action.payload,
        clientId: nextEvaluationFormClientId(),
      })
    },

    addEvaluationFormMemory: (state) => {
      state.evaluationsPage.form.evalMemories.push(
        toEvaluationFormMemory({
          path: "",
          key: "",
          value: "",
        })
      )
    },

    addEvaluationFormAssertion: (state) => {
      state.evaluationsPage.form.assertions.push({
        assertion: "",
        severity: "normal",
        clientId: nextEvaluationFormClientId(),
      })
    },

    removeEvaluationFormAssertion: (state, action: PayloadAction<number>) => {
      state.evaluationsPage.form.assertions =
        state.evaluationsPage.form.assertions.filter(
          (_, i) => i !== action.payload
        )
    },

    updateEvaluationFormAssertion: (
      state,
      action: PayloadAction<{
        index: number
        field: keyof EvaluationAssertion
        value: string
      }>
    ) => {
      const { index, field, value } = action.payload
      state.evaluationsPage.form.assertions[index][field] = value as never
    },

    loadEvaluationIntoForm: (
      state,
      action: PayloadAction<AgentEvaluationDetail>
    ) => {
      const e = action.payload
      state.evaluationsPage.form.name = e.name
      state.evaluationsPage.form.description = e.description ?? ""
      state.evaluationsPage.form.agentType = e.agent_type
      state.evaluationsPage.form.tags = e.tags ?? []
      state.evaluationsPage.form.tagsInput = (e.tags ?? []).join(", ")
      state.evaluationsPage.form.initialPrompt = e.initial_prompt
      state.evaluationsPage.form.maxIterations = e.max_iterations
      state.evaluationsPage.form.passingGradeThreshold =
        e.passing_grade_threshold ?? 80
      state.evaluationsPage.form.maxTurns = e.driver_config.max_turns ?? null
      state.evaluationsPage.form.launchTool =
        e.driver_config.launch_tool ?? null
      state.evaluationsPage.form.driverObjective =
        e.driver_config.driver_objective ?? ""
      state.evaluationsPage.form.chatConfigBot =
        e.driver_config.chat_config?.bot ?? ""
      state.evaluationsPage.form.chatConfigVertical =
        e.driver_config.chat_config?.vertical ?? ""
      state.evaluationsPage.form.chatConfigOrgid =
        e.driver_config.chat_config?.orgid ?? ""
      state.evaluationsPage.form.chatConfigTrip = toEvaluationFormTrip(
        e.driver_config.chat_config?.trip
      )
      state.evaluationsPage.form.evalKeyFacts = e.driver_config.key_facts ?? []
      state.evaluationsPage.form.evalMemories = (
        e.driver_config.memories ?? []
      ).map(toEvaluationFormMemory)
      state.evaluationsPage.form.preprocessInstruction =
        e.preprocess_instruction ?? ""
      state.evaluationsPage.form.followUpInstruction =
        e.follow_up_instruction ?? ""
      state.evaluationsPage.form.sessionUserId = e.session_user_id ?? null
      state.evaluationsPage.form.sessionUserLookup = e.session_user ?? null
      state.evaluationsPage.form.sessionUserInput = e.session_user_id
        ? String(e.session_user_id)
        : ""
      state.evaluationsPage.form.timeTargetSeconds =
        e.assertions.time_target_seconds ?? null
      state.evaluationsPage.form.timeTargetEnabled =
        e.assertions.time_target_seconds != null
      state.evaluationsPage.form.assertions =
        e.assertions.items.length > 0
          ? e.assertions.items.map(toEvaluationFormAssertion)
          : [
              {
                assertion: "",
                severity: "normal",
                clientId: nextEvaluationFormClientId(),
              },
            ]
      state.evaluationsPage.form.saving = false
      state.evaluationsPage.form.error = null
      state.evaluationsPage.form.loadingCloneSource = false
      state.evaluationsPage.form.generatedFrom = null
      state.evaluationsPage.form.generationExplanation = null
      state.evaluationsPage.form.generationExplanationNonce = null
    },

    resetEvaluationForm: (state) => {
      const empty = emptyEvaluationsPageState()
      state.evaluationsPage.form = empty.form
    },

    // Generate eval modal reducers
    openGenerateEvalModal: (
      state,
      action: PayloadAction<{
        sessionId: number
        initialMessage: string
        agentType: string
      }>
    ) => {
      const f = state.evaluationsPage.generateEvalForm
      f.open = true
      f.sessionId = action.payload.sessionId
      f.agentType =
        action.payload.agentType || state.evaluationsPage.form.agentType
      f.initialMessage = action.payload.initialMessage
      f.isResultGood = "yes"
      f.feedback = ""
      f.additionalNotes = ""
      f.autoGenerateAssertions = true
      f.autoGenerateDriverObjective = true
      f.autoGeneratePreprocessInstruction = true
      f.autoGenerateTags = true
      f.autoGenerateFollowUpInstruction = false
      f.loading = false
      f.error = null
    },

    closeGenerateEvalModal: (state) => {
      state.evaluationsPage.generateEvalForm.open = false
      state.evaluationsPage.generateEvalForm.loading = false
      state.evaluationsPage.generateEvalForm.error = null
    },

    setGenerateEvalField: (
      state,
      action: PayloadAction<{
        field: keyof Omit<
          GenerateEvalFormState,
          "open" | "loading" | "error" | "sessionId"
        >
        value: string | boolean
      }>
    ) => {
      const f = state.evaluationsPage.generateEvalForm as Record<
        string,
        unknown
      >
      f[action.payload.field] = action.payload.value
    },

    setGenerateEvalLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.generateEvalForm.loading = action.payload
    },

    setGenerateEvalError: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.generateEvalForm.error = action.payload
    },

    openEvalBuilder: (state) => {
      const f = state.evaluationsPage.evalBuilderForm
      const evalForm = state.evaluationsPage.form
      const hasMeaningfulEvalContent =
        evalForm.name.trim().length > 0 ||
        evalForm.description.trim().length > 0 ||
        evalForm.initialPrompt.trim().length > 0 ||
        evalForm.assertions.some((a) => a.assertion.trim().length > 0) ||
        evalForm.driverObjective.trim().length > 0 ||
        evalForm.chatConfigBot.trim().length > 0 ||
        evalForm.chatConfigVertical.trim().length > 0 ||
        evalForm.chatConfigOrgid.trim().length > 0 ||
        evalForm.chatConfigTrip !== null ||
        evalForm.evalKeyFacts.some((fact) => fact.trim().length > 0) ||
        evalForm.evalMemories.some(
          (memory) =>
            memory.path.trim().length > 0 ||
            memory.key.trim().length > 0 ||
            memory.value.trim().length > 0
        ) ||
        evalForm.preprocessInstruction.trim().length > 0 ||
        evalForm.followUpInstruction.trim().length > 0 ||
        evalForm.tags.length > 0

      f.open = true
      f.description = ""
      f.mode = hasMeaningfulEvalContent ? "refine" : "create"
      f.agentType = evalForm.agentType
      f.autoGenerateAssertions = true
      f.autoGenerateDriverObjective = true
      f.autoGeneratePreprocessInstruction = true
      f.autoGenerateTags = true
      f.autoGenerateFollowUpInstruction = false
      f.loading = false
      f.error = null
    },

    closeEvalBuilder: (state) => {
      state.evaluationsPage.evalBuilderForm.open = false
      state.evaluationsPage.evalBuilderForm.loading = false
      state.evaluationsPage.evalBuilderForm.error = null
    },

    setEvalBuilderField: (
      state,
      action: PayloadAction<{
        field: keyof Omit<EvalBuilderFormState, "open" | "loading" | "error">
        value: string | boolean
      }>
    ) => {
      const f = state.evaluationsPage.evalBuilderForm as Record<string, unknown>
      f[action.payload.field] = action.payload.value
    },

    setEvalBuilderLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.evalBuilderForm.loading = action.payload
    },

    setEvalBuilderError: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.evalBuilderForm.error = action.payload
    },

    loadGeneratedEvalIntoForm: (
      state,
      action: PayloadAction<{
        config: GeneratedEvalConfig
        source: "session" | "description"
      }>
    ) => {
      const { config: g, source } = action.payload
      const f = state.evaluationsPage.form
      f.name = g.name
      f.description = g.description ?? ""
      f.agentType = g.agent_type
      f.initialPrompt = g.initial_prompt
      f.assertions =
        g.assertions.items.length > 0
          ? g.assertions.items.map(toEvaluationFormAssertion)
          : [
              {
                assertion: "",
                severity: "normal",
                clientId: nextEvaluationFormClientId(),
              },
            ]
      f.driverObjective = g.driver_config.driver_objective ?? ""
      f.evalKeyFacts = g.driver_config.key_facts ?? []
      f.evalMemories = (g.driver_config.memories ?? []).map(
        toEvaluationFormMemory
      )
      f.preprocessInstruction = g.preprocess_instruction ?? ""
      f.tags = g.tags ?? []
      f.tagsInput = (g.tags ?? []).join(", ")
      f.maxIterations = null
      f.passingGradeThreshold = null
      f.maxTurns = g.driver_config.max_turns ?? null
      f.launchTool =
        g.agent_type === "Agent::MiniAgent"
          ? (g.driver_config.launch_tool ?? f.launchTool)
          : null
      f.chatConfigBot = g.driver_config.chat_config?.bot ?? f.chatConfigBot
      f.chatConfigVertical =
        g.driver_config.chat_config?.vertical ?? f.chatConfigVertical
      f.chatConfigOrgid =
        g.driver_config.chat_config?.orgid ?? f.chatConfigOrgid
      const trip = g.driver_config.chat_config?.trip
      if (trip !== undefined) {
        f.chatConfigTrip = toEvaluationFormTrip(trip)
      }
      f.followUpInstruction = g.follow_up_instruction ?? ""
      f.sessionUserId = null
      f.sessionUserLookup = null
      f.sessionUserInput = ""
      f.timeTargetSeconds = null
      f.timeTargetEnabled = false
      f.saving = false
      f.error = null
      f.loadingCloneSource = false
      f.generatedFrom = source
      f.generationExplanation = g.generation_explanation ?? null
      f.generationExplanationNonce = g.generation_explanation
        ? nowMillis()
        : null
    },

    setSelectedTags: (state, action: PayloadAction<string[]>) => {
      state.evaluationsPage.list.selectedTags = action.payload
    },

    toggleSelectedTag: (state, action: PayloadAction<string>) => {
      const tag = action.payload
      const current = state.evaluationsPage.list.selectedTags
      state.evaluationsPage.list.selectedTags = current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag]
    },

    clearSelectedTags: (state) => {
      state.evaluationsPage.list.selectedTags = []
    },

    setSelectedAgentType: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.list.selectedAgentType = action.payload
    },

    clearSelectedAgentType: (state) => {
      state.evaluationsPage.list.selectedAgentType = null
    },

    setAvailableTags: (state, action: PayloadAction<string[]>) => {
      state.evaluationsPage.list.availableTags = action.payload
    },

    setAvailableAgentTypes: (state, action: PayloadAction<AgentTypeInfo[]>) => {
      state.evaluationsPage.list.availableAgentTypes = action.payload
    },

    setEvaluationsListInitialized: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.list.initialized = action.payload
    },
    setDefaultModelId: (state, action: PayloadAction<string>) => {
      state.evaluationsPage.list.defaultModelId = action.payload
    },
    setSelectedModelId: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.list.selectedModelId = action.payload
    },

    // --- Eval Run Dialog ---

    openEvalRunDialog: (
      state,
      action: PayloadAction<{
        evaluationId: number
        agentType: string
        seedConfig?: {
          modelId?: string
          thinkingLevel?: ThinkingOverridePreset
        }
      }>
    ) => {
      const { evaluationId, agentType, seedConfig } = action.payload

      state.evaluationsPage.runDialog = {
        open: true,
        evaluationId,
        agentType,
        modelId: seedConfig?.modelId ?? null,
        thinkingLevel: seedConfig?.thinkingLevel ?? null,
        sessionConfig: {},
        running: false,
      }
    },

    closeEvalRunDialog: (state) => {
      state.evaluationsPage.runDialog = {
        open: false,
        evaluationId: null,
        agentType: null,
        modelId: null,
        thinkingLevel: null,
        sessionConfig: {},
        running: false,
      }
    },

    setEvalRunDialogModelId: (state, action: PayloadAction<string | null>) => {
      state.evaluationsPage.runDialog.modelId = action.payload
    },

    setEvalRunDialogThinkingLevel: (
      state,
      action: PayloadAction<ThinkingOverridePreset | null>
    ) => {
      state.evaluationsPage.runDialog.thinkingLevel = action.payload
    },

    setEvalRunDialogSessionConfigField: (
      state,
      action: PayloadAction<{ key: string; value: boolean | string }>
    ) => {
      state.evaluationsPage.runDialog.sessionConfig[action.payload.key] =
        action.payload.value
    },

    setEvalRunDialogRunning: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.runDialog.running = action.payload
    },

    // --- Eval Multi-Run Dialog ---

    openEvalMultiRunDialog: (state) => {
      state.evaluationsPage.multiRunDialog = emptyEvalMultiRunDialogState(
        true,
        true
      )
    },

    closeEvalMultiRunDialog: (state) => {
      state.evaluationsPage.multiRunDialog = emptyEvalMultiRunDialogState(
        false,
        false
      )
    },

    startMultiRunPickerRequest: (
      state,
      action: PayloadAction<MultiRunPickerRequest>
    ) => {
      const dialog = state.evaluationsPage.multiRunDialog
      const request = {
        ...action.payload,
        filterTags: [...action.payload.filterTags],
      }

      dialog.pickerRequest = request

      if (request.replace) {
        dialog.pickerError = null
        dialog.loadMorePickerError = null
        dialog.loadingEvaluations = true
        dialog.loadingMoreEvaluations = false
        dialog.availableRows = []
        dialog.pagingMeta = null
        dialog.matchingAgentTypes = []
      } else {
        dialog.loadMorePickerError = null
        dialog.loadingMoreEvaluations = true
      }
    },

    setMultiRunPickerResults: (
      state,
      action: PayloadAction<{
        response: GetAgentEvaluationPickerResponse
        request: MultiRunPickerRequest
      }>
    ) => {
      const dialog = state.evaluationsPage.multiRunDialog
      const request = action.payload.request
      if (!multiRunPickerRequestMatches(dialog, request)) return

      const rows = action.payload.response.evaluations

      if (request.replace) {
        dialog.availableRows = rows
      } else {
        const existingIds = new Set(dialog.availableRows.map((row) => row.id))
        dialog.availableRows = [
          ...dialog.availableRows,
          ...rows.filter((row) => !existingIds.has(row.id)),
        ]
      }

      dialog.pagingMeta = action.payload.response.paging_meta
      dialog.matchingAgentTypes = action.payload.response.matching_agent_types
      dialog.loadingEvaluations = false
      dialog.loadingMoreEvaluations = false
      dialog.pickerRequest = null
      dialog.pickerError = null
      dialog.loadMorePickerError = null
    },

    setMultiRunPickerFailure: (
      state,
      action: PayloadAction<{ request: MultiRunPickerRequest }>
    ) => {
      const dialog = state.evaluationsPage.multiRunDialog
      if (!multiRunPickerRequestMatches(dialog, action.payload.request)) return

      if (action.payload.request.replace) {
        dialog.loadingEvaluations = false
        dialog.availableRows = []
        dialog.pagingMeta = null
        dialog.matchingAgentTypes = []
        dialog.pickerError = "Failed to load evaluations"
        dialog.loadMorePickerError = null
      } else {
        dialog.loadingMoreEvaluations = false
        dialog.loadMorePickerError = "Failed to load more evaluations"
      }

      dialog.pickerRequest = null
    },

    setMultiRunSearchQuery: (state, action: PayloadAction<string>) => {
      state.evaluationsPage.multiRunDialog.searchQuery = action.payload
    },

    setMultiRunFilterTags: (state, action: PayloadAction<string[]>) => {
      state.evaluationsPage.multiRunDialog.filterTags = action.payload
    },

    setMultiRunFilterAgentType: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.evaluationsPage.multiRunDialog.filterAgentType = action.payload
    },

    toggleMultiRunEvaluation: (
      state,
      action: PayloadAction<AgentEvaluationPickerRow>
    ) => {
      const row = action.payload
      const dialog = state.evaluationsPage.multiRunDialog

      if (
        dialog.allMatching !== null &&
        pickerRowMatchesFilter(row, dialog.allMatching.filter)
      ) {
        dialog.explicitRows = dialog.explicitRows.filter((r) => r.id !== row.id)
        dialog.allMatching.excludedIds =
          dialog.allMatching.excludedIds.includes(row.id)
            ? dialog.allMatching.excludedIds.filter((id) => id !== row.id)
            : [...dialog.allMatching.excludedIds, row.id]
        return
      }

      dialog.explicitRows = dialog.explicitRows.some((r) => r.id === row.id)
        ? dialog.explicitRows.filter((r) => r.id !== row.id)
        : [...dialog.explicitRows, row]
    },

    selectLoadedMultiRunRows: (
      state,
      action: PayloadAction<AgentEvaluationPickerRow[]>
    ) => {
      const dialog = state.evaluationsPage.multiRunDialog
      const rows = action.payload

      if (dialog.allMatching !== null) {
        const allMatching = dialog.allMatching
        const selectedByAllMatching = rows.filter((row) =>
          pickerRowMatchesFilter(row, allMatching.filter)
        )
        const selectedRowIds = new Set(
          selectedByAllMatching.map((row) => row.id)
        )

        allMatching.excludedIds = allMatching.excludedIds.filter(
          (id) => !selectedRowIds.has(id)
        )
      }

      const explicitRowsById = new Map(
        dialog.explicitRows.map((row) => [row.id, row])
      )
      rows.forEach((row) => {
        if (
          dialog.allMatching === null ||
          !pickerRowMatchesFilter(row, dialog.allMatching.filter)
        ) {
          explicitRowsById.set(row.id, row)
        }
      })
      dialog.explicitRows = Array.from(explicitRowsById.values())
    },

    selectAllMatchingMultiRun: (state) => {
      const dialog = state.evaluationsPage.multiRunDialog
      if (dialog.allMatching !== null || dialog.pagingMeta === null) return

      const filter = currentMultiRunFilter(dialog)
      dialog.explicitRows = dialog.explicitRows.filter(
        (row) => !pickerRowMatchesFilter(row, filter)
      )
      dialog.allMatching = {
        filter,
        totalCount: dialog.pagingMeta.total_count,
        matchingAgentTypes: [...dialog.matchingAgentTypes],
        excludedIds: [],
      }
    },

    deselectAllMultiRun: (state) => {
      state.evaluationsPage.multiRunDialog.explicitRows = []
      state.evaluationsPage.multiRunDialog.allMatching = null
      state.evaluationsPage.multiRunDialog.modelIdByType = {}
      state.evaluationsPage.multiRunDialog.thinkingLevelByType = {}
      state.evaluationsPage.multiRunDialog.overridesByType = {}
    },

    removeMultiRunEvaluation: (state, action: PayloadAction<number>) => {
      state.evaluationsPage.multiRunDialog.explicitRows =
        state.evaluationsPage.multiRunDialog.explicitRows.filter(
          (row) => row.id !== action.payload
        )
    },

    setMultiRunModelIdForType: (
      state,
      action: PayloadAction<{ agentType: string; modelId: string }>
    ) => {
      state.evaluationsPage.multiRunDialog.modelIdByType[
        action.payload.agentType
      ] = action.payload.modelId
    },

    setMultiRunThinkingLevelForType: (
      state,
      action: PayloadAction<{
        agentType: string
        level: ThinkingOverridePreset
      }>
    ) => {
      state.evaluationsPage.multiRunDialog.thinkingLevelByType[
        action.payload.agentType
      ] = action.payload.level
    },

    setMultiRunOverrideForType: (
      state,
      action: PayloadAction<{
        agentType: string
        key: string
        value: boolean | string
      }>
    ) => {
      const { agentType, key, value } = action.payload
      if (!state.evaluationsPage.multiRunDialog.overridesByType[agentType]) {
        state.evaluationsPage.multiRunDialog.overridesByType[agentType] = {}
      }
      state.evaluationsPage.multiRunDialog.overridesByType[agentType][key] =
        value
    },

    setMultiRunIsRunning: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.multiRunDialog.isRunning = action.payload
    },

    setEvaluationAvailableModels: (
      state,
      action: PayloadAction<AgentModelOption[]>
    ) => {
      state.evaluationsPage.availableModels = action.payload
    },

    setEvaluationAvailableOrganizations: (
      state,
      action: PayloadAction<EvaluationOrganizationOption[]>
    ) => {
      state.evaluationsPage.availableOrganizations = action.payload
    },

    setSessionUserLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluationsPage.form.sessionUserLoading = action.payload
      if (action.payload) {
        state.evaluationsPage.form.sessionUserError = null
      }
    },

    setSessionUserLookupResult: (
      state,
      action: PayloadAction<EvaluationUserLookup>
    ) => {
      state.evaluationsPage.form.sessionUserId = action.payload.id
      state.evaluationsPage.form.sessionUserLookup = action.payload
      state.evaluationsPage.form.sessionUserLoading = false
      state.evaluationsPage.form.sessionUserError = null
    },

    setSessionUserError: (state, action: PayloadAction<string>) => {
      state.evaluationsPage.form.sessionUserError = action.payload
      state.evaluationsPage.form.sessionUserLoading = false
    },

    clearSessionUserLookup: (state) => {
      state.evaluationsPage.form.sessionUserId = null
      state.evaluationsPage.form.sessionUserLookup = null
      state.evaluationsPage.form.sessionUserInput = ""
      state.evaluationsPage.form.sessionUserLoading = false
      state.evaluationsPage.form.sessionUserError = null
    },
  },
})

const selectSliceState = (state: RootState) => state.internalAgenticSlice

export const selectAgenticDetailPage = createSelector(
  [selectSliceState],
  (state) => state.agenticDetailPage
)

export const selectAgenticDetailTitle = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    if (state.type === "existing" && state.session.title) {
      return state.session.title
    }
    if (state.type === "loading") {
      return "Loading Session…"
    }
    return "New Session"
  }
)

export const selectAgenticDetailLoading = createSelector(
  [selectAgenticDetailPage],
  (state) => state.loading
)

export const selectAgenticDetailStatus = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    if (state.type === "existing") {
      return state.session.status
    }
    if (state.type === "loading") {
      return "pending"
    }
    return "new"
  }
)

const IN_FLIGHT_JOB_STATES = ["delayed", "queued", "running"]

// The runner job's state when a pending session's job ended without running it, so nothing will pick it up.
export const selectAgenticDetailDeadRunnerJobState = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    if (state.type !== "existing" || state.session.status !== "pending") {
      return null
    }
    const jobState = state.session.pending_runner_job_state
    return jobState && !IN_FLIGHT_JOB_STATES.includes(jobState)
      ? jobState
      : null
  }
)

export const selectAgenticDetailError = createSelector(
  [selectAgenticDetailPage],
  (state) => (state.type === "error" ? state.error : null)
)

export const selectAgenticDetailMetrics = createSelector(
  [selectAgenticDetailPage],
  (state) => state.metrics
)

export const selectAgenticDetailMetricsExpanded = createSelector(
  [selectAgenticDetailPage],
  (state) => state.metricsExpanded
)

export const selectAgenticDetailType = createSelector(
  [selectAgenticDetailPage],
  (state) => state.type
)

export const selectAgenticDetailSession = createSelector(
  [selectAgenticDetailPage],
  (state) => ("session" in state ? state.session : null)
)

export const selectAgenticDetailSendingMessage = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sendingMessage
)

export const selectAgenticDetailStoppingSession = createSelector(
  [selectAgenticDetailPage],
  (state) => state.stoppingSession
)

export const selectAgenticDetailMessageError = createSelector(
  [selectAgenticDetailPage],
  (state) => state.messageError
)

export const selectAgenticDetailSelectedAgentType = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sessionForm.selectedAgentType
)

export const selectAgenticDetailAvailableAgentTypes = createSelector(
  [selectAgenticDetailPage],
  (state) => state.availableAgentTypes
)

const selectRawPlaygroundPermissions = (state: {
  internalAgenticSlice: InternalAgenticSliceState
}) => state.internalAgenticSlice.playgroundPermissions

export const selectPlaygroundPermissions = createSelector(
  [selectRawPlaygroundPermissions],
  (backendPermissions): FrontendPlaygroundPermissions => {
    if (!backendPermissions) return RESTRICTIVE_PLAYGROUND_PERMISSIONS

    return {
      canViewCost: backendPermissions.can_view_cost,
      canUseEvals: backendPermissions.can_use_evals,
      canUseAdvancedOptions: backendPermissions.can_use_advanced_options,
      canViewDebugPanels: backendPermissions.can_view_debug_panels,
      canToggleShare: backendPermissions.can_toggle_share,
      canViewAllAgents: backendPermissions.can_view_all_agents,
    }
  }
)

export const selectCanUseAdvancedOptions = createSelector(
  [selectPlaygroundPermissions],
  (permissions) => permissions.canUseAdvancedOptions
)

export const selectAgenticDetailSelectedModelId = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sessionForm.selectedModelId
)

export const selectAgenticDetailAvailableModels = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sessionForm.availableModels
)

export const selectAgenticDetailAllowedModels = createSelector(
  [selectAgenticDetailPage],
  (state) =>
    filterAllowedModelsForAgentType(
      state.sessionForm.availableModels,
      state.sessionForm.selectedAgentType,
      state.availableAgentTypes
    )
)

export const selectAgenticDetailStreamDrafts = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    const allDrafts = Object.values(state.streamDrafts || {})
    if (state.type !== "existing") {
      // In new/loading/error states there is no session to scope drafts to.
      // Return empty to avoid leaking drafts from a previous session.
      return []
    }

    const currentSessionId = state.session.id
    const isSubAgentSession = state.session.agent_class_type === "sub_agent"

    const scopedDrafts = allDrafts.filter((draft) => {
      if (isSubAgentSession) {
        return (
          draft.sessionId === currentSessionId ||
          draft.subAgentSessionId === currentSessionId
        )
      }

      return draft.sessionId === currentSessionId && !draft.subAgentSessionId
    })

    return scopedDrafts.sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))
  }
)

export const selectAgenticDetailMaxIterations = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sessionForm.maxIterations
)

export const selectAgenticDetailThinkingLevel = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sessionForm.thinkingLevel
)

export const selectAgenticDetailFormShared = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sessionForm.shared
)

export const selectAgenticDetailSessionConfigOverrides = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sessionForm.sessionConfigOverrides
)

export const selectPlaygroundConfigDescriptors = createSelector(
  [
    selectAgenticDetailSelectedAgentType,
    selectAgenticDetailAvailableAgentTypes,
  ],
  (agentType, availableAgentTypes) =>
    getSessionConfigDescriptorsForAgentType(agentType, availableAgentTypes)
)

export const selectPlaygroundSessionConfig = createSelector(
  [
    selectPlaygroundConfigDescriptors,
    selectAgenticDetailSessionConfigOverrides,
  ],
  (descriptors, overrides) =>
    buildSessionConfigFromDescriptors(descriptors, overrides)
)

export const selectAgenticDetailDefaultModelName = createSelector(
  [selectAgenticDetailPage],
  (state) =>
    "session" in state && state.type === "existing"
      ? state.session.default_model_name
      : null
)

export const selectAgenticDetailEvents = createSelector(
  [selectAgenticDetailPage],
  (state) => ("events" in state ? state.events : [])
)

export const selectAgenticDetailEventsLoading = createSelector(
  [selectAgenticDetailPage],
  (state) => ("eventsLoading" in state ? state.eventsLoading : false)
)

export const selectAgenticDetailHasMoreEvents = createSelector(
  [selectAgenticDetailPage],
  (state) => ("hasMoreEvents" in state ? state.hasMoreEvents : false)
)

export const selectAgenticDetailExpandedStates = createSelector(
  [selectAgenticDetailPage],
  (state) => state.expandedContentStates
)

export const selectAgenticDetailCurrentMode = createSelector(
  [selectAgenticDetailPage],
  (state) =>
    state.type === "existing" ? state.session.current_mode : undefined
)

export const selectAgenticDetailPendingPlan = createSelector(
  [selectAgenticDetailPage],
  (state) =>
    state.type === "existing" ? state.session.pending_plan : undefined
)

export const selectAgenticDetailPendingQuestions = createSelector(
  [selectAgenticDetailEvents],
  (events): AgentQuestionEventDetail | null => {
    const questionEvents = events.filter(isAgentQuestionEvent)
    const pendingQuestion = questionEvents.find((e) => e.status === "pending")
    return pendingQuestion ?? null
  }
)

export const selectAgenticDetailShowRejectInput = createSelector(
  [selectAgenticDetailPage],
  (state) => state.showRejectInput
)

export const selectAgenticDetailRejectReason = createSelector(
  [selectAgenticDetailPage],
  (state) => state.rejectReason
)

export const selectAgenticDetailActiveQuestionId = createSelector(
  [selectAgenticDetailPage],
  (state) => state.activeQuestionId
)

export const selectAgenticDetailSubAgentResponseEvent = createSelector(
  [selectAgenticDetailEvents],
  (events): AgentSubAgentResponseEventDetail | null => {
    const event = events.find(isAgentSubAgentResponseEvent)
    return event ?? null
  }
)

export const selectAgenticDetailStatusMessage = createSelector(
  [selectAgenticDetailPage],
  (state): string | null => {
    return state.type === "existing"
      ? (state.statusUpdate?.status_message ?? state.statusMessage)
      : null
  }
)

export const selectAgenticDetailStatusUpdate = createSelector(
  [selectAgenticDetailPage],
  (state): AgentStatusUpdateEventData | null => {
    return state.type === "existing" ? state.statusUpdate : null
  }
)

export const selectAgenticDetailMaxIterationsReached = createSelector(
  [selectAgenticDetailEvents],
  (events): boolean => {
    // Find the last assistant message event
    const assistantMessages = events.filter(
      (e): e is AgentMessageEventDetail =>
        e.type === "AgentMessageEvent" && e.role === "assistant"
    )
    if (assistantMessages.length === 0) return false

    const lastAssistantMessage = assistantMessages[assistantMessages.length - 1]
    // Only block on max_iterations_reached (safeguard limit)
    // Users CAN continue chatting after final_answer (natural completion)
    return lastAssistantMessage.metadata?.max_iterations_reached === true
  }
)

export const selectAgenticDetailSidebarOpen = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sidebarOpen
)

export const selectAgenticDetailSidebarExpanded = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sidebarExpanded
)

export const selectAgenticDetailSidebarSections = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sidebarSections
)

export const selectAgenticDetailSidebarViewMode = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sidebarViewMode
)

export const selectAgenticDetailSidebarScrollToEntityKey = createSelector(
  [selectAgenticDetailPage],
  (state) => state.sidebarScrollToEntityKey
)

export const selectAgenticDetailSelectedReportId = createSelector(
  [selectAgenticDetailPage],
  (state) => state.selectedReportId
)

export const selectAgenticDetailSubAgentIds = createSelector(
  [selectAgenticDetailEvents],
  (events): number[] => {
    return events
      .filter(isAgentToolCallResultEvent)
      .map((event) => event.metadata?.sub_agent_session_id)
      .filter((id): id is number => typeof id === "number")
  }
)

export const selectSubAgentMetrics = (state: RootState, sessionId: number) => {
  return state.internalAgenticSlice.sessionMetricsCache[sessionId]
}

export const selectAgenticDetailIsOwner = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    if (state.type !== "existing") return true
    return state.session.is_owner !== false
  }
)

export const selectAgenticDetailIsShared = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    if (state.type !== "existing") return false
    return state.session.shared
  }
)

export const selectAgenticDetailOwnerInfo = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    if (state.type !== "existing" || state.session.is_owner !== false)
      return null
    return { name: state.session.owner_name, email: state.session.owner_email }
  }
)

export const selectAgenticDetailOwner = createSelector(
  [selectAgenticDetailPage],
  (state) => {
    if (state.type !== "existing" || state.session.is_owner !== false)
      return undefined
    return state.session.owner
  }
)

export const selectAgenticDetailIsReadOnly = createSelector(
  [selectAgenticDetailIsOwner],
  (isOwner) => !isOwner
)

// --- Evaluation Selectors ---

export const selectEvaluationsPage = createSelector(
  [selectSliceState],
  (state) => state.evaluationsPage
)

export const selectEvaluationsList = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.evaluations
)

export const selectEvaluationsListPagingMeta = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.pagingMeta
)

export const selectEvaluationsListLoading = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.loading
)

export const selectEvaluationsListError = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.error
)

export const selectEvaluationDetail = createSelector(
  [selectEvaluationsPage],
  (state) => state.detail.evaluation
)

export const selectEvaluationDetailLoading = createSelector(
  [selectEvaluationsPage],
  (state) => state.detail.loading
)

export const selectEvaluationDetailError = createSelector(
  [selectEvaluationsPage],
  (state) => state.detail.error
)

export const selectEvaluationRuns = createSelector(
  [selectEvaluationsPage],
  (state) => state.detail.runs
)

export const selectEvaluationRunsPagingMeta = createSelector(
  [selectEvaluationsPage],
  (state) => state.detail.runsPagingMeta
)

export const selectEvaluationRunsLoading = createSelector(
  [selectEvaluationsPage],
  (state) => state.detail.runsLoading
)

export const selectEvaluationRunDetail = createSelector(
  [selectEvaluationsPage],
  (state) => state.runDetail.run
)

export const selectEvaluationRunDetailLoading = createSelector(
  [selectEvaluationsPage],
  (state) => state.runDetail.loading
)

export const selectEvaluationRunDetailError = createSelector(
  [selectEvaluationsPage],
  (state) => state.runDetail.error
)

export const selectEvaluationRunSessionEvents = createSelector(
  [selectEvaluationsPage],
  (state) => state.runDetail.sessionEvents
)

export const selectEvaluationRunSessionEventsLoading = createSelector(
  [selectEvaluationsPage],
  (state) => state.runDetail.sessionEventsLoading
)

export const selectEvaluationReportDetail = createSelector(
  [selectEvaluationsPage],
  (state) => state.reportDetail.report
)

export const selectEvaluationReportDetailLoading = createSelector(
  [selectEvaluationsPage],
  (state) => state.reportDetail.loading
)

export const selectEvaluationReportDetailError = createSelector(
  [selectEvaluationsPage],
  (state) => state.reportDetail.error
)

export const selectEvaluationRunKnownSessionIds = createSelector(
  [selectEvaluationRunDetail],
  (events): number[] => {
    if (!events) return []

    return Array.from(
      new Set(
        [
          events.agent_session_id,
          events.driver_session_id,
          events.grader_session_id,
          ...events.sub_agent_session_ids,
          ...events.chat_agent_session_ids,
        ].filter((id): id is number => typeof id === "number")
      )
    )
  }
)

export const selectChatTranscript = createSelector(
  [selectSliceState],
  (state) => state.chatTranscript.transcript
)

export const selectChatTranscriptLoading = createSelector(
  [selectSliceState],
  (state) => state.chatTranscript.loading
)

export const selectChatTranscriptError = createSelector(
  [selectSliceState],
  (state) => state.chatTranscript.error
)

export const selectEvaluationFormSaving = createSelector(
  [selectEvaluationsPage],
  (state) => state.form.saving
)

export const selectEvaluationFormError = createSelector(
  [selectEvaluationsPage],
  (state) => state.form.error
)

export const selectEvaluationFormLoadingClone = createSelector(
  [selectEvaluationsPage],
  (state) => state.form.loadingCloneSource
)

export const selectEvaluationForm = createSelector(
  [selectEvaluationsPage],
  (state) => state.form
)

export const selectEvaluationFormAssertions = createSelector(
  [selectEvaluationsPage],
  (state) => state.form.assertions
)

export const selectEvaluationFormGeneratedFrom = createSelector(
  [selectEvaluationsPage],
  (state) => state.form.generatedFrom
)

export const selectEvaluationFormGenerationExplanation = createSelector(
  [selectEvaluationsPage],
  (state) => state.form.generationExplanation
)

export const selectEvaluationFormGenerationExplanationNonce = createSelector(
  [selectEvaluationsPage],
  (state) => state.form.generationExplanationNonce
)

export const selectGenerateEvalForm = createSelector(
  [selectEvaluationsPage],
  (state) => state.generateEvalForm
)

export const selectGenerateEvalOpen = createSelector(
  [selectEvaluationsPage],
  (state) => state.generateEvalForm.open
)

export const selectEvalBuilderForm = createSelector(
  [selectEvaluationsPage],
  (state) => state.evalBuilderForm
)

export function buildChatConfig(
  form: EvaluationFormState
): EvaluationChatConfig | undefined {
  const chatConfig: EvaluationChatConfig = {
    bot: form.chatConfigBot.trim() || undefined,
    vertical: form.chatConfigVertical || undefined,
    orgid: form.chatConfigOrgid.trim() || undefined,
  }

  if (form.chatConfigTrip) {
    chatConfig.trip = {
      ...form.chatConfigTrip,
      major_stops: form.chatConfigTrip.major_stops.map(
        ({ entity_ref, days, stop_type }) => ({ entity_ref, days, stop_type })
      ),
    }
  }

  if (
    chatConfig.bot === undefined &&
    chatConfig.vertical === undefined &&
    chatConfig.orgid === undefined &&
    chatConfig.trip === undefined
  ) {
    return undefined
  }

  return chatConfig
}

export const selectEvaluationFormPayload = createSelector(
  [selectEvaluationsPage],
  (state) => {
    const form = state.form
    const chatConfig = buildChatConfig(form)
    const driverConfig: EvaluationDriverConfig = {
      driver_objective: form.driverObjective || null,
    }
    if (form.maxTurns !== null) {
      driverConfig.max_turns = form.maxTurns
    }
    if (form.launchTool) {
      driverConfig.launch_tool = form.launchTool
    }
    if (form.agentType === CHAT_EVAL_AGENT_TYPE && chatConfig) {
      driverConfig.chat_config = chatConfig
    }
    if (form.evalKeyFacts.length) {
      driverConfig.key_facts = form.evalKeyFacts
    }
    if (form.evalMemories.length) {
      driverConfig.memories = form.evalMemories.map(
        stripEvaluationMemoryClientId
      )
    }
    const payload: EvaluationFormPayload = {
      name: form.name,
      description: form.description || null,
      agent_type: form.agentType,
      initial_prompt: form.initialPrompt,
      passing_grade_threshold: form.passingGradeThreshold ?? undefined,
      driver_config: driverConfig,
      assertions: {
        items: form.assertions
          .filter((a) => a.assertion.trim())
          .map(stripEvaluationAssertionClientId),
        time_target_seconds: form.timeTargetSeconds ?? undefined,
      },
      tags: form.tags,
      preprocess_instruction: form.preprocessInstruction || null,
      follow_up_instruction: form.followUpInstruction.trim() || null,
      session_user_id: form.sessionUserId ?? null,
    }
    if (form.agentType !== CHAT_EVAL_AGENT_TYPE) {
      payload.max_iterations = form.maxIterations ?? undefined
    }
    return payload
  }
)

export const selectEvaluationFormValid = createSelector(
  [selectEvaluationsPage],
  (state): boolean => {
    const form = state.form
    const hasName = form.name.trim().length > 0
    const hasPrompt = form.initialPrompt.trim().length > 0
    const hasValidAssertion = form.assertions.some((a) => a.assertion.trim())
    const memoriesValid = form.evalMemories.every(
      (memory) => memory.path && memory.key && memory.value
    )
    const timeTargetValid =
      !form.timeTargetEnabled || form.timeTargetSeconds !== null
    return (
      hasName &&
      hasPrompt &&
      hasValidAssertion &&
      memoriesValid &&
      timeTargetValid
    )
  }
)

export type EvaluationFormErrors = {
  name: string | null
  initialPrompt: string | null
  assertions: string | null
  maxIterations: string | null
  passingGradeThreshold: string | null
  evalMemories: string | null
}

export const selectEvaluationFormErrors = createSelector(
  [selectEvaluationsPage],
  (state): EvaluationFormErrors => {
    const form = state.form
    return {
      name: form.name.trim().length === 0 ? "Name is required" : null,
      initialPrompt:
        form.initialPrompt.trim().length === 0
          ? "Initial prompt is required"
          : null,
      assertions: form.assertions.every((a) => !a.assertion.trim())
        ? "At least one assertion is required"
        : null,
      maxIterations:
        form.maxIterations !== null &&
        (form.maxIterations < 1 || form.maxIterations > 500)
          ? "Must be between 1 and 500"
          : null,
      passingGradeThreshold:
        form.passingGradeThreshold !== null &&
        (form.passingGradeThreshold < 0 || form.passingGradeThreshold > 100)
          ? "Must be between 0 and 100"
          : null,
      evalMemories: form.evalMemories.some(
        (memory) => !memory.path || !memory.key || !memory.value
      )
        ? "Each memory needs a path, key, and value"
        : null,
    }
  }
)

export const selectRunningEvaluationIds = createSelector(
  [selectEvaluationsPage],
  (state) => state.runningEvaluationIds
)

export const selectIsEvaluationRunning = (evaluationId: number) =>
  createSelector([selectRunningEvaluationIds], (runningIds) =>
    runningIds.includes(evaluationId)
  )

export const selectSelectedTags = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.selectedTags
)

export const selectSelectedAgentType = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.selectedAgentType
)

export const selectAvailableTags = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.availableTags
)

export const selectAvailableAgentTypes = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.availableAgentTypes
)

export const selectEvaluationsListInitialized = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.initialized
)

export const selectDefaultModelId = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.defaultModelId
)

export const selectSelectedModelId = createSelector(
  [selectEvaluationsPage],
  (state) => state.list.selectedModelId
)

// --- Available Models ---

export const selectEvaluationAvailableModels = createSelector(
  [selectEvaluationsPage],
  (state) => state.availableModels
)

export const selectEvaluationAvailableOrganizations = createSelector(
  [selectEvaluationsPage],
  (state) => state.availableOrganizations
)

// --- Run Dialog Selectors ---

export const selectEvalRunDialog = createSelector(
  [selectEvaluationsPage],
  (state) => state.runDialog
)

export const selectEvalRunDialogOpen = createSelector(
  [selectEvalRunDialog],
  (dialog) => dialog.open
)

export const selectEvalRunDialogEvaluationId = createSelector(
  [selectEvalRunDialog],
  (dialog) => dialog.evaluationId
)

export const selectEvalRunDialogAgentType = createSelector(
  [selectEvalRunDialog],
  (dialog) => dialog.agentType
)

export const selectEvalRunDialogModelId = createSelector(
  [selectEvalRunDialog],
  (dialog) => dialog.modelId
)

export const selectEvalRunDialogThinkingLevel = createSelector(
  [selectEvalRunDialog],
  (dialog) => dialog.thinkingLevel
)

export const selectEvalRunDialogSessionConfigOverrides = createSelector(
  [selectEvalRunDialog],
  (dialog) => dialog.sessionConfig
)

export const selectEvalRunDialogConfigDescriptors = createSelector(
  [selectEvalRunDialogAgentType, selectAvailableAgentTypes],
  (agentType, availableAgentTypes): SessionConfigUIDescriptor[] =>
    getSessionConfigDescriptorsForAgentType(agentType, availableAgentTypes)
)

export const selectEvalRunDialogShowsSessionConfig = createSelector(
  [selectEvalRunDialogConfigDescriptors],
  (descriptors) => descriptors.length > 0
)

export const selectEvalRunDialogSessionConfig = createSelector(
  [
    selectEvalRunDialogConfigDescriptors,
    selectEvalRunDialogSessionConfigOverrides,
  ],
  (descriptors, overrides) =>
    buildSessionConfigFromDescriptors(descriptors, overrides)
)

export const selectEvalRunDialogRunning = createSelector(
  [selectEvalRunDialog],
  (dialog) => dialog.running
)

export const selectEvalRunDialogAllowedModels = createSelector(
  [selectEvaluationsPage],
  (state) =>
    filterAllowedModelsForAgentType(
      state.availableModels,
      state.runDialog.agentType,
      state.list.availableAgentTypes
    )
)

export const selectEvalRunDialogDefaultThinking = createSelector(
  [selectEvaluationsPage],
  (state) => {
    const effectiveModelId =
      state.runDialog.modelId ??
      defaultModelIdForAgentType(
        state.availableModels,
        state.runDialog.agentType,
        state.list.availableAgentTypes
      )
    return resolveDefaultThinkingLevel(
      state.runDialog.agentType,
      effectiveModelId,
      state.list.availableAgentTypes
    )
  }
)

export const selectEvalRunDialogEffectiveModelId = createSelector(
  [selectEvaluationsPage],
  (state) =>
    state.runDialog.modelId ??
    defaultModelIdForAgentType(
      state.availableModels,
      state.runDialog.agentType,
      state.list.availableAgentTypes
    )
)

export const selectEvalRunDialogEffectiveThinking = createSelector(
  [selectEvaluationsPage],
  (state) => {
    if (state.runDialog.thinkingLevel !== null) {
      return state.runDialog.thinkingLevel
    }
    const effectiveModelId =
      state.runDialog.modelId ??
      defaultModelIdForAgentType(
        state.availableModels,
        state.runDialog.agentType,
        state.list.availableAgentTypes
      )
    return resolveDefaultThinkingLevel(
      state.runDialog.agentType,
      effectiveModelId,
      state.list.availableAgentTypes
    )
  }
)

export const selectEvalRunDialogRequestSessionConfig = createSelector(
  [
    selectEvalRunDialogAgentType,
    selectEvalRunDialogSessionConfig,
    selectEvalRunDialogThinkingLevel,
    selectEvalRunDialogEffectiveModelId,
  ],
  (agentType, sessionConfig, thinkingLevel, effectiveModelId) => {
    if (agentType === CHAT_EVAL_AGENT_TYPE) {
      return {}
    }

    const config: EvaluationSessionConfig = { ...sessionConfig }

    if (thinkingLevel !== null) {
      config.thinking_level = thinkingLevel
    }
    if (effectiveModelId) {
      config.model_id = effectiveModelId
    }

    return config
  }
)

// --- Multi-Run Dialog Selectors ---

export const selectMultiRunDialog = createSelector(
  [selectEvaluationsPage],
  (state) => state.multiRunDialog
)

export const selectMultiRunDialogOpen = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.open
)

export const selectMultiRunAvailableRows = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.availableRows
)

export const selectMultiRunPagingMeta = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.pagingMeta
)

export const selectMultiRunLoadingEvaluations = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.loadingEvaluations
)

export const selectMultiRunLoadingMoreEvaluations = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.loadingMoreEvaluations
)

export const selectMultiRunPickerError = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.pickerError
)

export const selectMultiRunLoadMorePickerError = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.loadMorePickerError
)

export const selectMultiRunMatchingAgentTypes = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.matchingAgentTypes
)

export const selectMultiRunSearchQuery = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.searchQuery
)

export const selectMultiRunFilterTags = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.filterTags
)

export const selectMultiRunFilterAgentType = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.filterAgentType
)

export const selectMultiRunIsRunning = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.isRunning
)

export const selectMultiRunModelIdByType = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.modelIdByType
)

export const selectMultiRunThinkingLevelByType = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.thinkingLevelByType
)

export const selectMultiRunOverridesByType = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.overridesByType
)

export const selectMultiRunSelectedEvaluations = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.explicitRows
)

export const selectMultiRunAllMatching = createSelector(
  [selectMultiRunDialog],
  (dialog) => dialog.allMatching
)

export const selectMultiRunSelectedCount = createSelector(
  [selectMultiRunDialog],
  (dialog) =>
    dialog.explicitRows.length +
    (dialog.allMatching
      ? dialog.allMatching.totalCount - dialog.allMatching.excludedIds.length
      : 0)
)

export const selectMultiRunSelectedAvailableRowIds = createSelector(
  [selectMultiRunDialog],
  (dialog) =>
    dialog.availableRows
      .filter((row) => multiRunRowIsSelected(dialog, row))
      .map((row) => row.id)
)

export const selectMultiRunSelectedAgentTypes = createSelector(
  [selectMultiRunDialog],
  (dialog) => [
    ...new Set([
      ...dialog.explicitRows.map((row) => row.agent_type),
      ...(dialog.allMatching?.matchingAgentTypes ?? []),
    ]),
  ]
)

export const selectMultiRunConfigDescriptorsByType = createSelector(
  [selectMultiRunSelectedAgentTypes, selectAvailableAgentTypes],
  (
    agentTypes,
    availableAgentTypes
  ): Record<string, SessionConfigUIDescriptor[]> =>
    Object.fromEntries(
      agentTypes.map((agentType) => [
        agentType,
        getSessionConfigDescriptorsForAgentType(agentType, availableAgentTypes),
      ])
    )
)

export const selectMultiRunRequestSessionConfigOverridesByType = createSelector(
  [
    selectMultiRunSelectedAgentTypes,
    selectMultiRunConfigDescriptorsByType,
    selectMultiRunOverridesByType,
    selectMultiRunModelIdByType,
    selectMultiRunThinkingLevelByType,
    selectEvaluationAvailableModels,
    selectAvailableAgentTypes,
  ],
  (
    agentTypes,
    configDescriptorsByType,
    overridesByType,
    modelIdByType,
    thinkingLevelByType,
    availableModels,
    availableAgentTypes
  ): Record<string, EvaluationSessionConfig> => {
    const byAgentType: Record<string, EvaluationSessionConfig> = {}

    for (const agentType of agentTypes) {
      const descriptors = configDescriptorsByType[agentType] || []
      const typeConfig = buildSessionConfigFromDescriptors(
        descriptors,
        overridesByType[agentType]
      )
      const resolvedModelId =
        modelIdByType[agentType] ??
        defaultModelIdForAgentType(
          availableModels,
          agentType,
          availableAgentTypes
        )

      if (resolvedModelId) {
        typeConfig.model_id = resolvedModelId
      }

      const userThinking = thinkingLevelByType[agentType]
      if (userThinking) {
        typeConfig.thinking_level = userThinking
      }

      if (Object.keys(typeConfig).length > 0) {
        byAgentType[agentType] = typeConfig
      }
    }

    return byAgentType
  }
)

export const selectMultiRunBatchSelection = createSelector(
  [selectMultiRunDialog],
  (dialog): RunEvaluationsBatchSelection => {
    if (dialog.allMatching) {
      return {
        mode: "all_matching",
        filter: {
          ...dialog.allMatching.filter,
          tags: [...dialog.allMatching.filter.tags],
        },
        excluded_evaluation_ids: [...dialog.allMatching.excludedIds],
        additional_evaluation_ids: dialog.explicitRows.map((row) => row.id),
      }
    }

    return {
      mode: "explicit",
      evaluation_ids: dialog.explicitRows.map((row) => row.id),
    }
  }
)

export const selectMultiRunAvailableTags = createSelector(
  [selectAvailableTags],
  (tags) => [...tags]
)

// Evaluation Runs Page Selectors
export const selectEvaluationRunsPage = createSelector(
  [(state: RootState) => state.internalAgenticSlice],
  (state) => state.evaluationRunsPage
)

export const selectAllEvaluationRuns = createSelector(
  [selectEvaluationRunsPage],
  (state) => state.runs
)

export const selectAllEvaluationRunsLoading = createSelector(
  [selectEvaluationRunsPage],
  (state) => state.loading
)

export const selectAllEvaluationRunsError = createSelector(
  [selectEvaluationRunsPage],
  (state) => state.error
)

export const selectAllEvaluationRunsPagingMeta = createSelector(
  [selectEvaluationRunsPage],
  (state) => state.pagingMeta
)

export const selectEvaluationRunsFilters = createSelector(
  [selectEvaluationRunsPage],
  (state) => state.filters
)

export const selectEvaluationRunsActiveFilterCount = createSelector(
  [selectEvaluationRunsFilters],
  (filters) => {
    let count = 0
    if (filters.runIds.length > 0) count++
    if (filters.evaluationId !== null) count++
    if (filters.status !== null) count++
    if (filters.fromDate !== null) count++
    if (filters.toDate !== null) count++
    return count
  }
)

export const {
  setAgenticDetailPage,
  setAgenticDetailSession,
  setAgenticDetailSendingMessage,
  setPlaygroundPermissions,
  setAgenticDetailOrgid,
  setAgenticDetailStoppingSession,
  setAgenticDetailLoading,
  setAgenticDetailMetricsExpanded,
  setAgenticDetailMessageError,
  setAgenticDetailSelectedAgentType,
  setAgenticDetailSelectedModelId,
  setAgenticDetailAvailableModels,
  applyAgenticStreamChunk,
  applyAgenticStreamChunkBatch,
  startPendingAgenticStreamDraft,
  clearPendingAgenticStreamDraft,
  clearCompletedAgenticStreamDrafts,
  setAgenticDetailMaxIterations,
  setAgenticDetailMaxIterationsFromInput,
  setAgenticDetailThinkingLevel,
  setAgenticDetailAvailableAgentTypes,
  setAgenticDetailEvents,
  appendAgenticDetailEvents,
  prependAgenticDetailEvents,
  setAgenticDetailEventsLoading,
  setAgenticDetailHasMoreEvents,
  setAgenticDetailStatus,
  setAgenticDetailTitle,
  setAgenticDetailCurrentMode,
  setAgenticDetailStatusMessage,
  setAgenticDetailStatusUpdate,
  setAgenticDetailPendingPlan,
  setAgenticDetailMetrics,
  setAgenticDetailEnhancedMetrics,
  setAgenticDetailMetricsLoading,
  setAgenticDetailMetricsError,
  toggleAgenticContentExpanded,
  setAgenticDetailShowRejectInput,
  setAgenticDetailRejectReason,
  resetAgenticDetailRejectState,
  setAgenticDetailActiveQuestionId,
  updateAgenticDetailQuestionEventStatus,
  setAgenticDetailSidebarOpen,
  setAgenticDetailSidebarExpanded,
  toggleAgenticDetailSidebarSection,
  setAgenticDetailSidebarViewMode,
  setAgenticDetailSidebarScrollToEntityKey,
  setAgenticDetailSelectedReportId,
  setSubAgentMetricsLoading,
  setSubAgentMetrics,
  setSubAgentMetricsError,
  setAgenticDetailShared,
  setAgenticDetailSessionConfigField,
  setAgenticDetailSessionShared,
  setAgenticDetailSocketConnected,
  setAgenticDetailPreviouslyConnected,
  setFlightOptionsLoading,
  setFlightOption,
  setFlightOptionsError,
  clearFlightOptionsCache,
  setFlightQuotesLoading,
  setFlightQuote,
  setFlightQuotesError,
  clearFlightQuotesCache,
  setFlights,
  clearFlightsCache,
  // Evaluation actions
  setEvaluationsListLoading,
  setEvaluationsListError,
  setEvaluationsList,
  updateEvaluationInList,
  removeEvaluationFromList,
  setEvaluationDetailLoading,
  setEvaluationDetailError,
  setEvaluationDetail,
  clearEvaluationDetail,
  setEvaluationRunsLoading,
  setEvaluationRuns,
  setEvaluationRunDetailLoading,
  setEvaluationRunDetailError,
  setEvaluationRunDetail,
  clearEvaluationRunDetail,
  setEvaluationReportDetailLoading,
  setEvaluationReportDetailError,
  setEvaluationReportDetail,
  clearEvaluationReportDetail,
  setEvaluationRunSessionEvents,
  appendEvaluationRunSessionEvents,
  setEvaluationRunSessionEventsLoading,
  setChatTranscriptLoading,
  setChatTranscriptError,
  setChatTranscript,
  clearChatTranscript,
  setEvaluationFormSaving,
  setEvaluationFormError,
  setEvaluationFormLoadingClone,
  clearEvaluationFormState,
  setEvaluationFormField,
  setEvaluationFormTags,
  addEvaluationFormMemory,
  addEvaluationFormTripStop,
  addEvaluationFormAssertion,
  removeEvaluationFormAssertion,
  updateEvaluationFormAssertion,
  loadEvaluationIntoForm,
  loadGeneratedEvalIntoForm,
  resetEvaluationForm,
  openGenerateEvalModal,
  closeGenerateEvalModal,
  setGenerateEvalField,
  setGenerateEvalLoading,
  setGenerateEvalError,
  openEvalBuilder,
  closeEvalBuilder,
  setEvalBuilderField,
  setEvalBuilderLoading,
  setEvalBuilderError,
  addRunningEvaluationId,
  removeRunningEvaluationId,
  resetEvaluationsPage,
  setSelectedTags,
  toggleSelectedTag,
  clearSelectedTags,
  setSelectedAgentType,
  clearSelectedAgentType,
  setAvailableTags,
  setAvailableAgentTypes,
  setEvaluationsListInitialized,
  setDefaultModelId,
  setSelectedModelId,
  // Run Dialog
  openEvalRunDialog,
  closeEvalRunDialog,
  setEvalRunDialogModelId,
  setEvalRunDialogThinkingLevel,
  setEvalRunDialogSessionConfigField,
  setEvalRunDialogRunning,
  // Multi-Run Dialog
  openEvalMultiRunDialog,
  closeEvalMultiRunDialog,
  startMultiRunPickerRequest,
  setMultiRunPickerResults,
  setMultiRunPickerFailure,
  setMultiRunSearchQuery,
  setMultiRunFilterTags,
  setMultiRunFilterAgentType,
  toggleMultiRunEvaluation,
  selectLoadedMultiRunRows,
  selectAllMatchingMultiRun,
  deselectAllMultiRun,
  removeMultiRunEvaluation,
  setMultiRunModelIdForType,
  setMultiRunThinkingLevelForType,
  setMultiRunOverrideForType,
  setMultiRunIsRunning,
  setEvaluationAvailableModels,
  setEvaluationAvailableOrganizations,
  setSessionUserLoading,
  setSessionUserLookupResult,
  setSessionUserError,
  clearSessionUserLookup,
  // Evaluation Runs Page actions
  setAllEvaluationRuns,
  setAllEvaluationRunsLoading,
  setAllEvaluationRunsError,
  setEvaluationRunsFilters,
  setEvaluationRunsFilter,
  clearEvaluationRunsFilters,
} = internalAgenticSlice.actions

export const selectAgenticDetailEnhancedMetrics = createSelector(
  [selectAgenticDetailPage],
  (state) => state.enhancedMetrics
)

export const selectAgenticDetailMetricsLoading = createSelector(
  [selectAgenticDetailPage],
  (state) => state.metricsLoading
)

export const selectAgenticDetailMetricsError = createSelector(
  [selectAgenticDetailPage],
  (state) => state.metricsError
)

export const selectAgenticDetailSocketConnected = createSelector(
  [selectAgenticDetailPage],
  (state) => state.socketConnected
)

export const selectAgenticDetailPreviouslyConnected = createSelector(
  [selectAgenticDetailPage],
  (state) => state.previouslyConnected
)

// Flight options cache selectors
export const selectFlightOptionsCache = createSelector(
  [selectSliceState],
  (state) => state.flightOptionsCache
)

export const selectFlightOptionEntry = (
  state: RootState,
  flightSearchResultId: number
): FlightOptionCacheEntry | undefined => {
  return state.internalAgenticSlice.flightOptionsCache[flightSearchResultId]
}

export const selectFlightOption = (
  state: RootState,
  flightSearchResultId: number
): FlightOptionWithMeta | null => {
  return (
    state.internalAgenticSlice.flightOptionsCache[flightSearchResultId]?.data ??
    null
  )
}

export const selectFlightOptionLoading = (
  state: RootState,
  flightSearchResultId: number
): boolean => {
  return (
    state.internalAgenticSlice.flightOptionsCache[flightSearchResultId]
      ?.loading ?? false
  )
}

export const selectFlightOptionError = (
  state: RootState,
  flightSearchResultId: number
): string | null => {
  return (
    state.internalAgenticSlice.flightOptionsCache[flightSearchResultId]
      ?.error ?? null
  )
}

// Select multiple flight options by IDs
export const selectFlightOptions = (
  state: RootState,
  ids: number[]
): FlightOptionWithMeta[] => {
  return ids
    .map((id) => state.internalAgenticSlice.flightOptionsCache[id]?.data)
    .filter(
      (flight): flight is FlightOptionWithMeta =>
        flight !== null && flight !== undefined
    )
}

// Select flight option entries by IDs
export const selectFlightOptionEntries = (
  state: RootState,
  ids: number[]
): FlightOptionCacheEntry[] => {
  return ids
    .map((id) => state.internalAgenticSlice.flightOptionsCache[id])
    .filter((entry): entry is FlightOptionCacheEntry => entry !== undefined)
}

// Check if any of the given IDs are loading
export const selectFlightOptionsLoading = (
  state: RootState,
  ids: number[]
): boolean => {
  return ids.some(
    (id) => state.internalAgenticSlice.flightOptionsCache[id]?.loading
  )
}

// Get first error from any of the given IDs
export const selectFlightOptionsError = (
  state: RootState,
  ids: number[]
): string | null => {
  for (const id of ids) {
    const error = state.internalAgenticSlice.flightOptionsCache[id]?.error
    if (error) return error
  }
  return null
}

// Flight quote cache selectors
export const selectFlightQuotesCache = createSelector(
  [selectSliceState],
  (state) => state.flightQuotesCache
)

export const selectFlightQuoteByRef = (
  state: RootState,
  ref: string
): FlightQuoteDetails | null => {
  return state.internalAgenticSlice.flightQuotesCache[ref]?.data ?? null
}

export const selectFlightQuoteLoading = (
  state: RootState,
  ref: string
): boolean => {
  return state.internalAgenticSlice.flightQuotesCache[ref]?.loading ?? false
}

export const selectFlightQuotesByRefs = (
  state: RootState,
  refs: string[]
): FlightQuoteDetails[] => {
  return refs
    .map((ref) => state.internalAgenticSlice.flightQuotesCache[ref]?.data)
    .filter((q): q is FlightQuoteDetails => q !== null && q !== undefined)
}

export const selectFlightQuotesLoading = (
  state: RootState,
  refs: string[]
): boolean => {
  return refs.some(
    (ref) => state.internalAgenticSlice.flightQuotesCache[ref]?.loading
  )
}

export const selectFlightQuotesError = (
  state: RootState,
  refs: string[]
): string | null => {
  for (const ref of refs) {
    const error = state.internalAgenticSlice.flightQuotesCache[ref]?.error
    if (error) return error
  }
  return null
}

// Flights cache selectors
export const selectFlightsCache = createSelector(
  [selectSliceState],
  (state) => state.flightsCache
)

// Select a single flight by ID from the flights cache
export const selectFlightById = (
  state: RootState,
  flightId: number
): FlightLegApi | undefined => {
  return state.internalAgenticSlice.flightsCache[flightId]
}

export default internalAgenticSlice
